

/**
 * 童顏家行程日曆系統 - 主應用程式
 * 
 * 版本：V1.32.2 | 更新日期：2025/10/02
 * 
 * 檔案功能：系統主入口，整合所有功能模組，支援區域營運商權限
 * 解決問題：提供統一的應用程式介面，管理路由、狀態與多角色權限
 * 
 * 更新內容：
 * - V1.32.2: 修復更新夥伴時 role 欄位 undefined 錯誤 - 確保更新時自動根據 isAdmin 設定預設值
 * - V1.32.1: 修復新增夥伴時 role 欄位 undefined 錯誤 - 新增 newVendorRole 狀態定義並設定預設值為 'partner'
 * - V1.32.0: 新增 operator（營運商）角色，支援區域性管理
 * - V1.31.0: 日曆顯示權限限制 - 根據夥伴區域權限篩選日曆和日期詳情顯示的預約
 * - V1.30.0: 簡化權限介面顯示 - 移除區域權限提示顯示，保持權限篩選功能但簡化介面
 * - V1.29.0: 縣市選擇權限限制功能 - 根據夥伴區域權限篩選可選擇的縣市，確保縣市和市場選擇一致性
 * - V1.28.0: 預約登記權限限制功能 - 整合區域權限系統到預約登記，根據夥伴權限篩選可登記市場
 * - V1.27.0: 市場管理區域分類功能 - 整合區域權限系統到市場管理，支援自動區域分類和篩選
 * - V1.26.0: 夥伴區域權限管理功能 - 整合區域權限系統到夥伴管理，支援多區域權限設定和顯示
 * - V1.25.0: 管理員幫夥伴刪除登記功能 - 管理員可在日期詳情中幫任何夥伴刪除登記，包含重複預定刪除
 * - V1.24.0: 重複排班選單優化 - 將重複排班選項改為下拉選單，支援動態星期顯示（如：每週星期二、每隔週星期二等）
 * - V1.23.0: 新增照片位置地圖功能 - 點擊拍照位置可開啟地圖應用，支援 iOS/Android/桌面版
 * - V1.22.0: 新增攤位登記彈窗優化 - 新增關閉按鈕和滾動功能，改善使用者體驗
 * - V1.21.0: 備註權限限制 - 只有登入使用者才能看到擺攤登記的備註內容
 * - V1.20.0: 拍照權限限制 - 只有登入使用者才能看到拍照功能和照片預覽
 * - V1.19.0: 拍照日期限制 - 只有當天日期才能拍照，過去和未來日期都無法拍照
 * - V1.18.0: 拍照功能優化 - 移除拍照後確認彈窗，新增照片刪除權限限制（只能刪除當天照片）
 * - V1.17.0: 照片查看功能優化 - 新增照片旋轉和放大縮小功能，優化手機版顯示，新增日期照片預覽
 * - V1.16.0: 新增拍照位置市場偵測功能 - 根據拍照位置自動偵測1公里範圍內的市場，支援市場經緯度管理
 * - V1.15.0: 拍照功能優化 - 移至夥伴日期登記內容中，管理員也可使用，新增拍照位置資訊
 * - V1.14.0: 新增管理員照片管理功能，支援查看、下載和刪除夥伴拍照留底
 * - V1.13.9: 新增夥伴拍照留底功能，支援相機權限檢查和自動儲存
 * - V1.13.8: 推播通知標籤優化，支援推播累加顯示
 * - V1.13.7: 管理員幫夥伴登記推播標題簡化為直接顯示管理員和夥伴名稱
 * - V1.13.6: 管理員幫夥伴登記推播內容顯示實際管理員名稱
 * - V1.13.5: 管理員幫夥伴登記推播內容進一步簡化，移除重複排班和備註資訊
 * - V1.13.4: 管理員幫夥伴登記推播內容簡化為更簡潔的格式
 * - V1.13.3: 管理員幫夥伴登記推播通知改為發送給全部夥伴和管理員
 * - V1.13.2: 管理員幫夥伴登記新增推播通知功能，只通知該夥伴
 * - V1.13.1: 管理功能優化 - 幫夥伴登記移至夥伴管理上方，新增重複排班和備註功能
 * - V1.13.0: 管理員可幫夥伴新增擺攤登記（管理面板新增快速登記區塊）
 * - V1.12.0: 新增可自訂重複次數功能，支援 2-12 次的重複排班選項
 * - V1.11.9: 新增隔週重複排班選項，支援每兩週一次的排班模式
 * - V1.11.8: 新增環境變數檢查，支援測試/正式環境識別
 * - V1.11.0: 智慧推薦系統升級
 * - V1.10.6: 天氣預報系統整合
 * - V1.08: 庫存管理系統與說明彈窗
 * 
 * 注意事項：
 * - 環境變數：REACT_APP_ENV 用於識別部署環境
 * - 關連檔案：InventoryPage.js, firebase-messaging-sw.js
 * - 拍照功能：需要相機權限，夥伴和管理員都可使用，自動儲存到Firestore，支援位置資訊
 * - 照片管理：管理員可查看、下載和刪除夥伴拍照留底
 * - operator 權限僅限於自己 regions 內的資料
 */

import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, doc, onSnapshot, addDoc, updateDoc, deleteDoc, serverTimestamp, query, where, writeBatch, getDocs, setDoc, getDoc, orderBy, limit } from 'firebase/firestore';
import { getMessaging, getToken } from 'firebase/messaging';
import { getFunctions, httpsCallable } from 'firebase/functions';
// 模組化組件導入
import { CalendarGrid } from './modules/Calendar';
import { PersonalizedRecommendations } from './modules/Recommendation';
import { Header } from './modules/Header';
import { Announcements } from './modules/Announcement';
import { MarketQuickSearch } from './modules/MarketQuickSearch';
import { SearchResultModal } from './modules/SearchResultModal';
import { ConfirmationModal } from './modules/ConfirmationModal';
import { WeatherModal } from './modules/WeatherModal';
import { GeminiModal } from './modules/GeminiModal';
import { PWAInstallPrompt } from './modules/PWAInstallPrompt';
import { CameraModal } from './modules/CameraModal';
import { PhotoViewModal } from './modules/PhotoViewModal';
import { PhotoPreviewModal } from './modules/PhotoPreviewModal';
import { LoginModal, AccountModal, ResetPasswordModal } from './modules/Auth';
import { BookingModal, AddMarketForm } from './modules/Booking';
import SalesInput from './modules/SalesInput/SalesInput';
// 【AI新增】區域權限系統導入
// Why: 整合區域權限功能到夥伴管理系統
import { ALL_REGIONS, REGION_CODES } from './constants/regions';
import { formatRegionsForDisplay, isValidRegionsArray, getRegionByCity, getRegionDisplayName, getRegionIcon } from './utils/regionUtils';
import { createUniqueMarket, findDuplicateMarketInFirestore, getMarketRegion } from './utils/marketUtils';
import RegionSelector from './modules/RegionPermission/RegionSelector';
import { isAdmin, isOperator } from './utils/roleUtils'; // 【AI修正】引入 operator 判斷
import { logMarketOperation } from './modules/MarketOperationLogs/marketOperationLogService';

// 庫存管理只會在管理員進入 /inventory 時使用。延後載入可縮小首頁首次下載量。
const InventoryPage = lazy(() => import('./InventoryPage'));

// TAIWAN_CITIES 常數
const TAIWAN_CITIES = [ "宜蘭縣", "花蓮縣", "臺東縣", "澎湖縣", "金門縣", "連江縣", "臺北市", "新北市", "桃園市", "臺中市", "臺南市", "高雄市", "基隆市", "新竹縣", "新竹市", "苗栗縣", "彰化縣", "南投縣", "雲林縣", "嘉義縣", "嘉義市", "屏東縣" ];

const toLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getMonthDateRange = (date) => ({
    start: toLocalDateString(new Date(date.getFullYear(), date.getMonth(), 1)),
    end: toLocalDateString(new Date(date.getFullYear(), date.getMonth() + 1, 0))
});

// 【AI新增】根據經緯度計算兩點間距離的函數 (Haversine 公式)
// Why: 用於計算拍照位置與市場之間的實際距離，確保準確性
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // 地球半徑 (公里)
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // 返回公里數
};

// 【AI新增】查找1公里範圍內最近市場的函數
// Why: 根據拍照位置自動偵測夥伴實際所在的市場，即使夥伴在別的市場拍照也能正確記錄
const findNearbyMarket = (photoLat, photoLon, markets) => {
    let nearestMarket = null;
    let minDistance = Infinity;

    markets.forEach(market => {
        // 檢查市場是否有經緯度資料
        if (market.latitude && market.longitude) {
            const distance = calculateDistance(photoLat, photoLon, market.latitude, market.longitude);
            
            // 如果距離在1公里內且比之前找到的更近
            if (distance <= 1 && distance < minDistance) {
                minDistance = distance;
                nearestMarket = {
                    ...market,
                    distance: distance
                };
            }
        }
    });

    return nearestMarket;
};

// ===========================================
// --- App 主元件 ---
// ===========================================
// 【AI承諾】此元件是整個應用程式的根元件，負責統一管理所有狀態和功能
// Why: 採用單一狀態管理模式，確保資料一致性和可維護性

const App = () => {
    // ===========================================
    // --- 狀態管理 (State Management) ---
    // ===========================================
    // 【AI保證】每個狀態都有明確的用途和生命週期

    // 核心系統狀態
    // Why: 這些狀態控制整個應用程式的基礎運作
    const [currentUser, setCurrentUser] = useState(null);          // 當前登入用戶資訊
    const [isAuthReady, setIsAuthReady] = useState(false);         // Firebase 認證是否就緒
    const [db, setDb] = useState(null);                           // Firestore 資料庫實例
    const [functions, setFunctions] = useState(null);             // Firebase Functions 實例
    const [appId, setAppId] = useState(null);                     // Firebase 專案 ID
    
    // 業務資料狀態
    // Why: 這些狀態儲存從 Firebase 載入的業務資料，支援離線快取
    const [vendors, setVendors] = useState([]);                   // 攤商夥伴列表
    const [markets, setMarkets] = useState([]);                   // 市場列表
    const [bookings, setBookings] = useState([]);                 // 預約記錄列表
    const [announcements, setAnnouncements] = useState([]);       // 公告列表
    const [pushNotifications, setPushNotifications] = useState([]); // 推播通知記錄

    
    // UI 控制狀態
    // Why: 這些狀態控制用戶介面的顯示和互動行為
    const [currentDate, setCurrentDate] = useState(new Date());   // 日曆當前顯示月份
    const [isLoading, setIsLoading] = useState(true);             // 載入狀態指示器
    const [error, setError] = useState(null);                     // 錯誤訊息顯示

    // PWA 安裝提示狀態
    // 【AI標準】PWA 安裝提示採用物件結構，統一管理相關狀態
    // Why: 提供完整的 PWA 安裝體驗，讓用戶能方便地將應用程式加到主畫面
    const [installPrompt, setInstallPrompt] = useState({
        isAvailable: false,          // 是否可安裝 - 瀏覽器是否支援且滿足安裝條件
        isVisible: false,            // 是否顯示提示 - 控制 UI 顯示時機
        deferredPrompt: null         // 延遲的安裝提示事件 - 瀏覽器提供的安裝介面
    });

    // ===========================================
    // --- Modal 彈窗狀態管理 ---
    // ===========================================
    // 【AI保證】每個 Modal 都有統一的開關控制和資料傳遞機制
    // Why: 採用一致的 Modal 狀態結構，簡化狀態管理和程式碼維護
    
    const [dayDetail, setDayDetail] = useState({ isOpen: false, date: null });                    // 日期詳情彈窗
    const [bookingModal, setBookingModal] = useState({ isOpen: false, date: null, booking: null }); // 預約編輯彈窗
    const [loginModal, setLoginModal] = useState({ isOpen: false });                              // 登入彈窗
    const [accountModal, setAccountModal] = useState({ isOpen: false });                          // 帳號管理彈窗
    const [resetPasswordModal, setResetPasswordModal] = useState({ isOpen: false, vendor: null }); // 密碼重設彈窗
    const [confirmation, setConfirmation] = useState({ isOpen: false, title: '', message: '', onConfirm: null }); // 確認對話框
    const [geminiModal, setGeminiModal] = useState({ isOpen: false, content: '', isLoading: false, error: '' }); // AI 助理彈窗
    const [searchResultModal, setSearchResultModal] = useState({ isOpen: false, data: null });   // 搜尋結果彈窗
    const [weatherModal, setWeatherModal] = useState({ isOpen: false, city: '', date: '', marketName: '' }); // 天氣彈窗

    // 【AI新增】拍照留底功能狀態管理
    // Why: 管理相機權限、拍照狀態和照片儲存流程
    const [cameraModal, setCameraModal] = useState({ 
        isOpen: false, 
        isCapturing: false, 
        hasPermission: false, 
        error: '' 
    }); // 拍照留底彈窗

    // 【AI新增】照片預覽功能狀態管理
    // Why: 管理照片預覽彈窗，支援在日期詳情中查看夥伴照片
    const [photoPreviewModal, setPhotoPreviewModal] = useState({ 
        isOpen: false, 
        photo: null 
    }); // 照片預覽彈窗

    // 【AI新增】照片管理狀態
    // Why: 管理夥伴拍攝的照片，支援在日期詳情中預覽
    const [vendorPhotos, setVendorPhotos] = useState([]);
    const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

    // App.js 頂層初始化
    const [photoViewModal, setPhotoViewModal] = useState({ isOpen: false, photo: null });

   
    // ===========================================
    // --- PWA 安裝提示事件監聽 ---
    // ===========================================
    // 【AI承諾】此 useEffect 負責監聽和處理 PWA 安裝相關事件
    // Why: 捕獲瀏覽器的安裝提示事件，讓我們能自訂安裝 UI 和時機，提供更好的用戶體驗
    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {
            // 關鍵情境一：阻止預設的瀏覽器安裝提示
            // Why: 讓我們能控制何時顯示安裝提示，提供更好的用戶體驗
            e.preventDefault();
            console.log('🔧 PWA 安裝條件已滿足，儲存安裝提示事件');
            
            setInstallPrompt({
                isAvailable: true,
                isVisible: false,
                deferredPrompt: e
            });
            
            // 關鍵情境二：延遲顯示安裝提示
            // Why: 避免立即干擾用戶，在用戶使用一段時間後再提示安裝
            setTimeout(() => {
                setInstallPrompt(prev => ({
                    ...prev,
                    isVisible: true
                }));
            }, 10000); // 10秒後顯示
        };

        // 關鍵情境三：監聽應用程式安裝事件
        // Why: 當用戶成功安裝 PWA 後，隱藏安裝提示並自動註冊推播通知
        const handleAppInstalled = () => {
            console.log('🎉 PWA 已成功安裝');
            setInstallPrompt({
                isAvailable: false,
                isVisible: false,
                deferredPrompt: null
            });
            localStorage.setItem('pwaInstalled', 'true');
            
            // 【AI新增】PWA 安裝後自動註冊推播通知
            // Why: 用戶安裝 PWA 表示希望獲得更好的體驗，包含推播通知
            setTimeout(() => {
                console.log('🚀 PWA 安裝完成，準備自動註冊推播通知...');
                // 清除所有用戶的推播註冊記錄，強制重新註冊
                const keys = Object.keys(localStorage);
                keys.forEach(key => {
                    if (key.startsWith('fcmTokenRegistered_')) {
                        localStorage.removeItem(key);
                        console.log(`🗑️ 清除推播記錄: ${key}`);
                    }
                });
                
                // 觸發頁面重新載入以重新註冊推播
                console.log('🔄 重新載入頁面以註冊推播通知...');
                window.location.reload();
            }, 2000); // 等待 2 秒讓 PWA 完全載入
        };

        // 關鍵情境四：檢測 PWA 是否真的已安裝
        // Why: 即使 localStorage 記錄已安裝，PWA 可能已被用戶刪除，需要重新檢測
        const checkPWAInstallation = () => {
            // 檢查是否在 standalone 模式運行（已安裝的 PWA）
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
            const isInWebAppiOS = (window.navigator.standalone === true);
            const isInstalled = isStandalone || isInWebAppiOS;
            
            if (isInstalled) {
                console.log('✅ PWA 目前已安裝並運行中');
                localStorage.setItem('pwaInstalled', 'true');
                return true;
            } else {
                // 如果不在 standalone 模式，清除安裝記錄
                // Why: PWA 可能已被用戶刪除，需要重新提供安裝機會
                localStorage.removeItem('pwaInstalled');
                console.log('🔍 PWA 未在 standalone 模式運行，清除安裝記錄');
                return false;
            }
        };

        // 檢查是否已安裝
        if (checkPWAInstallation()) {
            console.log('✅ PWA 已安裝，不顯示安裝提示');
            return;
        }

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    // ===========================================
    // --- Firebase 初始化 & 認證 ---
    // ===========================================
    // 【AI保證】此 useEffect 負責 Firebase 服務的完整初始化流程
    // Why: 建立與 Firebase 的連接，包含認證、資料庫和雲端函數服務
    useEffect(() => {
        const initializeFirebase = async () => {
            try {
                let firebaseConfig;
                
                // 關鍵情境一：檢測運行環境並使用適當的 Firebase 配置
                // Why: 本地開發環境無法訪問 /__/firebase/init.json，需要使用硬編碼配置
                try {
                    // 嘗試從 Firebase Hosting 載入配置（生產環境）
                    const response = await fetch('/__/firebase/init.json');
                    if (response.ok) {
                        firebaseConfig = await response.json();
                        console.log('✅ 從 Firebase Hosting 載入配置');
                    } else {
                        throw new Error('Firebase Hosting 配置不可用');
                    }
                } catch (hostingError) {
                    // 關鍵情境二：本地開發環境回退配置
                    // Why: 確保本地開發環境能正常運作，使用專案的實際 Firebase 配置
                    console.log('⚠️ Firebase Hosting 配置不可用，使用本地開發配置');
                    // 【AI新增】本地開發也能根據網址自動切換 projectId
                    let dynamicProjectId = 'tyj-vendor-system';
                    if (window.location.host.includes('test')) {
                      dynamicProjectId = 'tyj-vendor-system-test';
                    }
                    firebaseConfig = {
                        apiKey: "AIzaSyC8haDlva7XeEBWUtZ99EUcsKdg4q9Dyk8",
                        authDomain: `${dynamicProjectId}.firebaseapp.com`,
                        projectId: dynamicProjectId,
                        storageBucket: `${dynamicProjectId}.appspot.com`,
                        messagingSenderId: "355400967927",
                        appId: "1:355400967927:web:12c469569c88befbe155f2"
                    };
                }

                const app = initializeApp(firebaseConfig);
                const authInstance = getAuth(app);
                const dbInstance = getFirestore(app);
                const functionsInstance = getFunctions(app);

                setDb(dbInstance);
                setFunctions(functionsInstance);
                setAppId(firebaseConfig.projectId); // 只用 firebaseConfig.projectId

                onAuthStateChanged(authInstance, (user) => {
                    if (user) {
                        setIsAuthReady(true);
                    } else {
                        signInAnonymously(authInstance).catch((e) => {
                            console.error("Anonymous sign-in failed:", e);
                            setError("無法取得匿名登入權限，請檢查 Firebase 安全規則。");
                        });
                    }
                });
            } catch (e) {
                console.error("Firebase initialization failed:", e);
                setError("Firebase 初始化失敗：" + e.message);
                setIsLoading(false);
            }
        };

        initializeFirebase();
    }, []);

    // ===========================================
    // --- 推播通知設定 ---
    // ===========================================
    // 🛡️【重要警告】此區塊為推播通知核心功能，絕對不可刪除或移動！
    // 📋 如意外修改請參考：PUSH_NOTIFICATION_PROTECTION.md
    // 【AI標準】此 useEffect 處理 FCM 推播通知的完整設定流程
    // Why: 在 Firebase 認證完成後設定推播通知，確保用戶能接收重要訊息
    useEffect(() => {
        if (!isAuthReady || !db || !appId || !currentUser) return;
        
        const setupNotifications = async () => {
            if ('Notification' in window && 'serviceWorker' in navigator) {
                try {
                    console.log('🔔 開始設定推播通知...');
                    
                    // 🛡️【絕對不可修改】註冊 Service Worker - 推播通知核心
                    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                    console.log('✅ Service Worker 註冊成功:', registration.scope);
                    
                    // 等待 Service Worker 啟動
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    
                    // 請求通知權限
                    const permission = await Notification.requestPermission();
                    console.log('📱 通知權限狀態:', permission);
                    
                    if (permission === 'granted') {
                        // 獲取全域 messaging 實例
                        const messaging = getMessaging();
                        
                        // 嘗試取得 FCM Token（先不使用 VAPID 金鑰）
                        let currentToken;
                        // 檢查 Firebase Auth 狀態
                        const auth = getAuth();
                        console.log('🔐 檢查 Firebase Auth 狀態:', {
                            currentUser: auth.currentUser,
                            uid: auth.currentUser?.uid,
                            isAnonymous: auth.currentUser?.isAnonymous,
                            isAuthReady: isAuthReady
                        });
                        
                        // 確保 Firebase Auth 完全就緒
                        if (!auth.currentUser) {
                            console.log('⚠️ Firebase Auth 用戶不存在，嘗試重新登入...');
                            try {
                                await signInAnonymously(auth);
                                console.log('✅ 匿名登入成功');
                            } catch (authError) {
                                console.error('❌ 匿名登入失敗:', authError);
                                throw new Error(`Firebase Auth 失敗: ${authError.message}`);
                            }
                        }
                        
                        try {
                            console.log('🔑 嘗試不使用 VAPID Key 取得 Token...');
                            currentToken = await getToken(messaging);
                            
                            if (!currentToken) {
                                console.log('🔑 無 VAPID Key 失敗，嘗試使用 VAPID Key...');
                                currentToken = await getToken(messaging, {
                                    vapidKey: 'BMvAspKI4uTAeaGge_dDWsf-YcSzhbM7iNy6f9dgx71UFlG1NFJLpsTFShOtIOBgwh-R00Mp-1VF77j1NRLAR50'
                                });
                            }
                        } catch (tokenError) {
                            console.error('❌ 取得 FCM Token 時發生錯誤:', tokenError);
                            throw new Error(`FCM Token 取得失敗: ${tokenError.message}`);
                        }
                        
                        if (currentToken) {
                            console.log('🎯 FCM Token 取得成功:', currentToken.substring(0, 20) + '...');
                            
                            // 🛡️【絕對不可修改】FCM Token 資料結構 - 推播通知核心
                            const tokenData = {
                                userId: currentUser.id,  // 攤商 ID
                                timestamp: serverTimestamp(),
                                userAgent: navigator.userAgent,
                                createdAt: new Date().toISOString(),
                                deviceType: /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop'
                            };
                            
                            try {
                                // 儲存到 Firestore - 添加詳細日誌
                                console.log('📝 準備儲存 FCM Token:', {
                                    path: `artifacts/${appId}/fcmTokens/${currentToken}`,
                                    data: tokenData
                                });
                                
                                await setDoc(doc(db, 'artifacts', appId, 'fcmTokens', currentToken), tokenData);
                                console.log('✅ FCM Token 已儲存到 Firestore');
                                
                                // 驗證是否真的儲存成功
                                const savedDoc = await getDoc(doc(db, 'artifacts', appId, 'fcmTokens', currentToken));
                                if (savedDoc.exists()) {
                                    console.log('✅ 驗證：文檔確實已儲存到 Firestore');
                                } else {
                                    console.error('❌ 驗證失敗：文檔未找到！');
                                }
                                
                                // 只在成功儲存後才設定 localStorage
                                const registeredKey = `fcmTokenRegistered_${currentUser.id}`;
                                localStorage.setItem(registeredKey, 'yes');
                                
                                console.log('🎉 推播通知已成功啟用！');
                            } catch (saveError) {
                                console.error('❌ 儲存 FCM Token 失敗:', saveError);
                                console.log('⚠️ 推播通知設定失敗: ' + saveError.message);
                                alert('推播通知設定失敗: ' + saveError.message);
                            }
                        } else {
                            console.error('❌ 無法取得 FCM Token');
                            console.log('⚠️ 無法取得推播 Token，請檢查瀏覽器設定');
                        }
                    } else {
                        console.log('📵 用戶拒絕通知權限');
                    }
                } catch (err) {
                    console.error('❌ 推播通知設定失敗:', err);
                    console.log('⚠️ 推播通知設定失敗: ' + err.message);
                }
            } else {
                console.log('📵 瀏覽器不支援推播通知');
            }
        };
        
        // 檢查是否已經註冊過（使用攤商 ID）
        const registeredKey = `fcmTokenRegistered_${currentUser.id}`;
        
        if (localStorage.getItem(registeredKey) !== 'yes') {
            console.log('🚀 用戶已登入且未註冊推播，開始設定...');
            // 延遲執行，確保所有服務都已就緒
            setTimeout(setupNotifications, 2000);
        } else {
            console.log('✅ 推播通知已註冊過');
        }
    }, [isAuthReady, db, appId, currentUser]);

    // --- 資料庫讀取 ---
    useEffect(() => {
        if (!isAuthReady || !db || !appId) return;
        
        const vendorsRef = collection(db, `artifacts/${appId}/public/data/vendors`);

        const unsubscribes = [
            onSnapshot(vendorsRef, (s) => {
                const fetchedVendors = s.docs.map(d => ({ id: d.id, ...d.data() }));
                setVendors(fetchedVendors);
                const savedVendorId = localStorage.getItem('tyjVendorId');
                if (savedVendorId) {
                    const savedVendor = fetchedVendors.find(v => v.id === savedVendorId);
                    if (savedVendor) {
                        if (savedVendor.isActive === false) {
                            // 如果夥伴被停用，自動登出
                            handleLogout();
                            alert('您的帳號已被停用，請聯繫管理員。');
                        } else {
                            setCurrentUser(savedVendor);
                        }
                    }
                }
            }, (err) => { console.error("讀取夥伴資料失敗:", err); setError("讀取夥伴資料失敗，請檢查網路或權限設定。"); }),
            
            onSnapshot(collection(db, `artifacts/${appId}/public/data/markets`), (s) => setMarkets(s.docs.map(d => ({ id: d.id, ...d.data() })))),
            onSnapshot(query(collection(db, `artifacts/${appId}/public/data/announcements`), orderBy("createdAt", "desc")), (s) => setAnnouncements(s.docs.map(d => ({ id: d.id, ...d.data() }))))
        ];
        
        const timer = setTimeout(() => setIsLoading(false), 1500);
        
        return () => {
            clearTimeout(timer);
            unsubscribes.forEach(unsub => unsub());
        };
    }, [isAuthReady, db, appId]);

    // 首頁日曆只維持目前月份的即時資料，避免每次開啟都下載全部歷史預約。
    // 歷史資料會在「我的帳號」或管理操作需要時改由各功能單獨查詢。
    useEffect(() => {
        if (!isAuthReady || !db || !appId) return;

        const { start, end } = getMonthDateRange(currentDate);
        setBookings([]);
        const monthlyBookingsQuery = query(
            collection(db, `artifacts/${appId}/public/data/bookings`),
            where('date', '>=', start),
            where('date', '<=', end),
            orderBy('date', 'asc')
        );

        return onSnapshot(
            monthlyBookingsQuery,
            (snapshot) => setBookings(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => {
                console.error(`讀取 ${start} 至 ${end} 的預約資料失敗:`, err);
                setError('讀取本月預約資料失敗，請檢查網路或權限設定。');
            }
        );
    }, [isAuthReady, db, appId, currentDate]);

    // 推播紀錄只會在管理員面板使用；一般首頁不再下載整份歷史紀錄。
    // 保留最近 100 筆，避免紀錄長期累積後拖慢管理員開啟首頁。
    useEffect(() => {
        if (!db || !appId || !isAdmin(currentUser)) {
            setPushNotifications([]);
            return;
        }

        const notificationsQuery = query(
            collection(db, `artifacts/${appId}/public/data/pushNotifications`),
            orderBy("createdAt", "desc"),
            limit(100)
        );

        return onSnapshot(
            notificationsQuery,
            (snapshot) => setPushNotifications(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
            (err) => console.error("讀取推播紀錄失敗:", err)
        );
    }, [db, appId, currentUser]);

    // ===========================================
    // --- 事件處理函式 ---
    // ===========================================
    // 【AI保證】所有事件處理都包含完整的狀態管理和錯誤處理
    
    // 登入成功處理
    // 【AI標準】統一的登入成功流程，包含狀態設定和本地儲存
    // Why: 設定當前用戶並關閉登入彈窗，同時儲存用戶 ID 供自動登入使用
    const handleLoginSuccess = (vendor) => {
        setCurrentUser(vendor);                    // 設定當前登入用戶狀態
        localStorage.setItem('tyjVendorId', vendor.id); // 儲存用戶 ID 供下次自動登入
        setLoginModal({ isOpen: false });         // 關閉登入彈窗
    };
    
    // 登出處理
    // 【AI承諾】確保完全清除用戶會話和相關資料
    // Why: 清除用戶會話和本地儲存，確保資料安全和隱私保護
    const handleLogout = () => {
        setCurrentUser(null);                      // 清除當前用戶狀態
        localStorage.removeItem('tyjVendorId');    // 清除本地儲存的用戶 ID
    };
    
    // 重設推播通知
    // 【AI標準】提供用戶重新註冊推播的功能，解決通知問題
    // Why: 當推播通知出現問題時，允許用戶重新設定，提升用戶體驗
    const handleResetPush = () => {
        if (window.confirm('確定要重新註冊推播通知嗎？這會清除目前的註冊狀態。')) {
            const currentUserId = currentUser?.id || 'anonymous';
            const registeredKey = `fcmTokenRegistered_${currentUserId}`;
            localStorage.removeItem(registeredKey); // 清除推播註冊記錄
            window.location.reload();              // 重新載入頁面以重新註冊
        }
    };

    // ===========================================
    // --- PWA 安裝提示處理函數 ---
    // ===========================================
    // 【AI承諾】提供完整且用戶友善的 PWA 安裝體驗
    // Why: 引導用戶安裝 PWA 到主畫面，提供原生應用程式般的使用體驗
    
    // PWA 安裝處理
    // 【AI保證】完整的安裝流程處理，包含成功、失敗和錯誤情況
    // Why: 使用瀏覽器原生的安裝流程，確保安裝過程順暢且符合標準
    const handleInstallPWA = async () => {
        if (!installPrompt.deferredPrompt) return; // 檢查是否有可用的安裝提示
        
        try {
            // 關鍵情境一：觸發瀏覽器安裝提示
            // Why: 使用瀏覽器原生的安裝流程，確保安裝過程順暢
            const result = await installPrompt.deferredPrompt.prompt();
            console.log('🚀 PWA 安裝提示結果:', result.outcome);
            
            if (result.outcome === 'accepted') {
                // 用戶接受安裝
                console.log('✅ 用戶接受安裝 PWA');
                setInstallPrompt({
                    isAvailable: false,    // 標記為不可用
                    isVisible: false,      // 隱藏提示
                    deferredPrompt: null   // 清除延遲提示
                });
            } else {
                // 用戶取消安裝
                console.log('❌ 用戶取消安裝 PWA');
                handleInstallClose();      // 關閉提示但保留下次顯示機會
            }
        } catch (error) {
            // 安裝過程錯誤處理
            console.error('❌ PWA 安裝過程發生錯誤:', error);
        }
    };

    // PWA 安裝提示關閉處理
    // 【AI標準】只關閉當前提示，不影響下次顯示機會
    // Why: 不強制用戶永久隱藏，保持未來的安裝機會，提高轉換率
    const handleInstallClose = () => {
        setInstallPrompt(prev => ({
            ...prev,
            isVisible: false          // 只隱藏當前提示，保留其他狀態
        }));
        
        console.log('❌ 用戶關閉 PWA 安裝提示（下次進入還會顯示）');
    };

    // 【AI新增】拍照留底功能處理函數
    // Why: 提供完整的拍照流程，包含權限檢查、拍照和自動儲存
    // 【AI修改】拍照功能移至夥伴日期登記內容中
    // Why: 拍照功能應該與具體的擺攤登記關聯，而不是獨立功能
    const handleOpenCamera = async (bookingInfo = null) => {
        if (!currentUser) {
            alert('請先登入才能使用拍照功能');
            return;
        }

        try {
            // 檢查瀏覽器是否支援相機
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setCameraModal({
                    isOpen: true,
                    isCapturing: false,
                    hasPermission: false,
                    error: '您的瀏覽器不支援相機功能',
                    bookingInfo: bookingInfo
                });
                return;
            }

            // 請求相機權限
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: { 
                    facingMode: 'environment', // 使用後置相機
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                } 
            });

            setCameraModal({
                isOpen: true,
                isCapturing: false,
                hasPermission: true,
                error: '',
                stream: stream,
                bookingInfo: bookingInfo
            });

        } catch (error) {
            console.error('相機權限請求失敗:', error);
            setCameraModal({
                isOpen: true,
                isCapturing: false,
                hasPermission: false,
                error: '無法取得相機權限，請在瀏覽器設定中允許相機存取',
                bookingInfo: bookingInfo
            });
        }
    };

    // 【AI修改】拍照功能移至夥伴日期登記內容中
    // Why: 拍照功能應該與具體的擺攤登記關聯，而不是獨立功能
    const handleCapturePhoto = async (bookingInfo = null) => {
        if (!cameraModal.stream) return;

        setCameraModal(prev => ({ ...prev, isCapturing: true }));

        try {
            // 創建 video 元素來顯示相機畫面
            const video = document.createElement('video');
            video.srcObject = cameraModal.stream;
            video.play();

            // 等待視頻載入
            await new Promise(resolve => {
                video.onloadedmetadata = () => resolve();
            });

            // 創建 canvas 來截取照片
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');

            // 截取照片
            ctx.drawImage(video, 0, 0);
            const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);

            // 停止相機
            cameraModal.stream.getTracks().forEach(track => track.stop());

            // 自動儲存照片（關聯到具體的擺攤登記）
            await savePhotoToFirestore(photoDataUrl, bookingInfo);

            // 關閉拍照彈窗
            setCameraModal({
                isOpen: false,
                isCapturing: false,
                hasPermission: false,
                error: ''
            });

        } catch (error) {
            console.error('拍照失敗:', error);
            setCameraModal(prev => ({
                ...prev,
                isCapturing: false,
                error: '拍照失敗，請重試'
            }));
        }
    };

    // 【AI修改】拍照功能移至夥伴日期登記內容中
    // Why: 拍照功能應該與具體的擺攤登記關聯，而不是獨立功能
    const savePhotoToFirestore = async (photoDataUrl, bookingInfo = null) => {
        try {
            // 生成照片ID
            const photoId = `photo_${currentUser.id}_${Date.now()}`;
            // 準備照片資料
            let city = bookingInfo?.marketCity;
            let region = city ? getRegionByCity(city) : null;
            // 若 bookingInfo 沒有 city，嘗試從 market 查找
            if (!city && bookingInfo?.marketName) {
                const market = markets.find(m => m.name === bookingInfo.marketName);
                if (market) {
                    city = market.city;
                    region = getMarketRegion(market);
                }
            }
            const photoData = {
                vendorId: currentUser.id,
                vendorName: currentUser.name,
                photoDataUrl: photoDataUrl,
                capturedAt: serverTimestamp(),
                timestamp: new Date().toISOString(),
                location: null, // 將由 getCurrentPosition 填充
                bookingInfo: bookingInfo ? {
                    date: bookingInfo.date,
                    marketName: bookingInfo.marketName,
                    marketCity: city
                } : null,
                // 【AI新增】自動補 region 欄位
                region: region
            };
            
            // 【AI新增】嘗試取得拍照位置
            if (navigator.geolocation) {
                try {
                    const position = await new Promise((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, {
                            enableHighAccuracy: true,
                            timeout: 10000,
                            maximumAge: 60000
                        });
                    });
                    
                    photoData.location = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy
                    };

                    // 【AI新增】根據拍照位置查找1公里範圍內的市場
                    const nearbyMarket = findNearbyMarket(position.coords.latitude, position.coords.longitude, markets);
                    if (nearbyMarket) {
                        photoData.detectedMarket = {
                            id: nearbyMarket.id,
                            name: nearbyMarket.name,
                            city: nearbyMarket.city,
                            distance: nearbyMarket.distance
                        };
                        // 若 region 尚未設定，優先用 detectedMarket.city
                        if (!photoData.region && nearbyMarket.city) {
                            photoData.region = getRegionByCity(nearbyMarket.city);
                        }
                    }
                } catch (geoError) {
                    console.log('無法取得位置資訊:', geoError);
                    // 位置取得失敗不影響照片儲存
                }
            }

            // 儲存到 Firestore
            await addDoc(collection(db, `artifacts/${appId}/public/data/vendorPhotos`), photoData);

            // 【AI新增】重新載入照片列表
            await loadVendorPhotos();

            console.log('✅ 照片已自動儲存到 Firestore');
            // 【AI修改】移除確認彈窗，改為自動關閉
            // alert('📸 攤位照片已自動儲存！');

        } catch (error) {
            console.error('❌ 儲存照片失敗:', error);
            alert('儲存照片失敗: ' + error.message);
        }
    };

    const handleCloseCamera = () => {
        // 停止相機
        if (cameraModal.stream) {
            cameraModal.stream.getTracks().forEach(track => track.stop());
        }

        setCameraModal({
            isOpen: false,
            isCapturing: false,
            hasPermission: false,
            error: ''
        });
    };

    // 【AI新增】載入夥伴照片
    // Why: 載入所有夥伴拍攝的照片，支援在日期詳情中預覽
    const loadVendorPhotos = async () => {
        if (!db || !appId) return;
        setIsLoadingPhotos(true);
        try {
            const photosQuery = query(
                collection(db, `artifacts/${appId}/public/data/vendorPhotos`),
                orderBy('timestamp', 'desc')
            );
            const photosSnapshot = await getDocs(photosQuery);
            const photosData = photosSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setVendorPhotos(photosData);
        } catch (error) {
            console.error('載入照片失敗:', error);
        } finally {
            setIsLoadingPhotos(false);
        }
    };

    // 【AI新增】載入照片的 useEffect
    // Why: 當資料庫和應用程式ID準備好時，自動載入照片
    useEffect(() => {
        if (db && appId) {
            loadVendorPhotos();
        }
    }, [db, appId]);

    const handleDayClick = (date) => setDayDetail({ isOpen: true, date });
    const openBookingModal = (date) => {
        setBookingModal({ isOpen: true, date, booking: null });
        setDayDetail({ isOpen: false, date: null });
    };
    
    // 【AI修正】刪除擺攤登記功能 - 支援管理員幫夥伴刪除
    // Why: 讓管理員也能幫夥伴刪除登記，同時保持原有的重複預定刪除邏輯
    const handleDeleteBooking = (booking) => {
        // 檢查是否為重複預定（有 recurringId）
        if (booking.recurringId) {
            const isAdminDeleting = currentUser?.isAdmin && currentUser?.id !== booking.vendorId;
            const adminPrefix = isAdminDeleting ? `幫 ${booking.vendorName} ` : '';
            
            setConfirmation({
                isOpen: true,
                title: isAdminDeleting ? '管理員刪除重複預定' : '刪除重複預定',
                message: (
                    <div className="text-center">
                        <div className="mb-4">
                            <p className="text-lg font-medium text-gray-800">這項預定是重複預定</p>
                            <p className="text-sm text-gray-600 mt-1">
                                {booking.date} - {booking.marketName}
                            </p>
                            {isAdminDeleting && (
                                <p className="text-sm text-blue-600 mt-1">
                                    管理員操作：{booking.vendorName}
                                </p>
                            )}
                        </div>
                        <p className="text-sm text-gray-600">
                            請選擇要刪除的範圍：
                        </p>
                    </div>
                ),
                customButtons: [
                    { 
                        label: '取消', 
                        onClick: () => setConfirmation({ isOpen: false }) 
                    },
                    { 
                        label: `${adminPrefix}只刪除這項預定`, 
                        isDanger: true, 
                        onClick: async () => {
                            try {
                                const batch = writeBatch(db);
                                batch.delete(doc(db, `artifacts/${appId}/public/data/bookings`, booking.id));
                                await batch.commit();
                                setDayDetail({ isOpen: false, date: null });
                                
                                if (isAdminDeleting) {
                                    alert(`✅ 已成功幫 ${booking.vendorName} 刪除這項預定`);
                                } else {
                                    alert('✅ 已成功刪除這項預定');
                                }
                            } catch (err) {
                                console.error('刪除失敗:', err);
                                alert("刪除失敗：" + err.message);
                            }
                        }
                    },
                    { 
                        label: `${adminPrefix}刪除這之後的預定`, 
                        isDanger: true, 
                        onClick: async () => {
                            try {
                                // 首頁只保留當月資料；刪除重複預約時必須額外查詢完整系列。
                                const recurringSnapshot = await getDocs(query(
                                    collection(db, `artifacts/${appId}/public/data/bookings`),
                                    where('recurringId', '==', booking.recurringId)
                                ));
                                const bookingsToDelete = recurringSnapshot.docs
                                    .filter(item => item.data().date >= booking.date)
                                    .map(item => item.id);

                                for (let index = 0; index < bookingsToDelete.length; index += 500) {
                                    const batch = writeBatch(db);
                                    bookingsToDelete.slice(index, index + 500).forEach(id => {
                                        batch.delete(doc(db, `artifacts/${appId}/public/data/bookings`, id));
                                    });
                                    await batch.commit();
                                }
                                setDayDetail({ isOpen: false, date: null });
                                
                                const deleteCount = bookingsToDelete.length;
                                if (isAdminDeleting) {
                                    alert(`✅ 已成功幫 ${booking.vendorName} 刪除 ${deleteCount} 項預定`);
                                } else {
                                    alert(`✅ 已成功刪除 ${deleteCount} 項預定`);
                                }
                            } catch (err) {
                                console.error('刪除失敗:', err);
                                alert("刪除失敗：" + err.message);
                            }
                        }
                    }
                ]
            });
        } else {
            // 非重複預定，直接刪除
            const isAdminDeleting = currentUser?.isAdmin && currentUser?.id !== booking.vendorId;
            const deleteMessage = isAdminDeleting 
                ? `您確定要幫 ${booking.vendorName} 刪除 ${booking.date} 在 ${booking.marketName} 的登記嗎？`
                : `您確定要刪除 ${booking.date} 在 ${booking.marketName} 的登記嗎？`;
            
            setConfirmation({
                isOpen: true,
                title: isAdminDeleting ? '管理員刪除夥伴登記' : '刪除擺攤登記',
                message: deleteMessage,
                onConfirm: async () => {
                    try {
                        await deleteDoc(doc(db, `artifacts/${appId}/public/data/bookings`, booking.id));
                        setDayDetail({ isOpen: false, date: null });
                        
                        if (isAdminDeleting) {
                            alert(`✅ 已成功幫 ${booking.vendorName} 刪除預定`);
                        } else {
                            alert('✅ 預定已刪除');
                        }
                    } catch (err) {
                        console.error('刪除失敗:', err);
                        alert("刪除失敗：" + err.message);
                    }
                }
            });
        }
    };

    // 【AI新增】照片刪除函數 - 移至主應用程式
    // Why: 讓夥伴和管理員都能在日期詳情中刪除照片
    const handleDeletePhoto = (photo) => {
        // 【AI新增】檢查照片拍攝日期，只能刪除當天拍攝的照片
        const photoDate = photo.timestamp ? new Date(photo.timestamp) : null;
        const today = new Date();
        const isToday = photoDate && 
            photoDate.getFullYear() === today.getFullYear() &&
            photoDate.getMonth() === today.getMonth() &&
            photoDate.getDate() === today.getDate();

        if (!isToday) {
            alert('⚠️ 只能刪除今天拍攝的照片！');
            return;
        }

        setConfirmation({
            isOpen: true,
            title: '刪除照片',
            message: `確定要刪除 ${photo.vendorName} 在 ${photo.timestamp ? new Date(photo.timestamp).toLocaleString('zh-TW') : '未知時間'} 拍攝的照片嗎？`,
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, `artifacts/${appId}/public/data/vendorPhotos`, photo.id));
                    setVendorPhotos(prev => prev.filter(p => p.id !== photo.id));
                    alert('照片已刪除');
                } catch (error) {
                    console.error('刪除照片失敗:', error);
                    alert('刪除照片失敗: ' + error.message);
                }
            }
        });
    };

    // --- 主應用程式畫面 ---
    if (error) {
        return (
            <div className="text-center p-10 bg-red-100 text-red-700">
                <h1 className="text-2xl font-bold">系統發生嚴重錯誤</h1>
                <p className="mt-4">{error}</p>
            </div>
        );
    }

    // 【AI修正】operator 也能進入管理面板，資料過濾
    // Why: operator 只能管理自己 regions 內的夥伴、登記、照片
    // 【AI新增】未登入狀態下顯示全省資料
    // Why: 讓未登入用戶能看到全省的市場和預約資訊
    const getOperatorRegions = () => (currentUser?.regions && Array.isArray(currentUser.regions)) ? currentUser.regions : [];
    const isOperatorMode = isOperator(currentUser);
    const isAdminMode = isAdmin(currentUser);
    
    // 【AI修正】未登入狀態下顯示全省資料
    // Why: 未登入用戶應該能看到所有市場和預約，不受區域限制
    const isNotLoggedIn = !currentUser;
    
    // 依 operator 權限過濾夥伴
    const filteredVendors = isAdminMode ? vendors : isOperatorMode ? vendors.filter(v => v.regions && v.regions.some(r => getOperatorRegions().includes(r))) : vendors;
    
    // 依 operator 權限過濾市場
    const filteredMarkets = isAdminMode ? markets : isOperatorMode ? markets.filter(m => {
        const region = getMarketRegion(m);
        return getOperatorRegions().includes(region);
    }) : markets;
    
    // 依 operator 權限過濾預約
    const filteredBookings = isAdminMode ? bookings : isOperatorMode ? bookings.filter(b => {
        const market = markets.find(m => m.id === b.marketId);
        const region = getMarketRegion(market);
        return getOperatorRegions().includes(region);
    }) : bookings;
    
    // 依 operator 權限過濾照片
    const filteredVendorPhotos = isAdminMode ? vendorPhotos : isOperatorMode ? vendorPhotos.filter(photo => {
        // 只顯示自己區域的夥伴照片
        const vendor = vendors.find(v => v.id === photo.vendorId);
        return vendor && vendor.regions && vendor.regions.some(r => getOperatorRegions().includes(r));
    }) : vendorPhotos;

    return (
        <Router>
            <Routes>
                <Route path="/" element={
                    <>
                        <div className="min-h-screen bg-gray-100 p-2 sm:p-6 lg:p-8 font-sans">
                            <div className="max-w-4xl mx-auto bg-white sm:rounded-2xl sm:shadow-lg p-4 sm:p-6">
                                <Header 
                                    currentUser={currentUser} 
                                    onLogout={handleLogout} 
                                    onLoginClick={() => setLoginModal({ isOpen: true })} 
                                    onAccountClick={() => setAccountModal({ isOpen: true })}
                                />
                                <MarketQuickSearch markets={filteredMarkets} bookings={filteredBookings} onSearchResult={setSearchResultModal} />
                                {(isLoading && vendors.length === 0) ? (
                                     <div className="text-center p-10 text-gray-500">
                                        <p>系統資料載入中，請稍候...</p>
                                    </div>
                                ) : (
                                    <>
                                        {currentUser && <Announcements announcements={announcements} />}
                                        <PersonalizedRecommendations currentUser={currentUser} bookings={filteredBookings} markets={filteredMarkets} functions={functions} appId={appId} />
                                        <CalendarGrid currentDate={currentDate} setCurrentDate={setCurrentDate} bookings={filteredBookings} onDayClick={handleDayClick} appId={appId} db={db} currentUser={currentUser} functions={functions} markets={filteredMarkets} />
                                        {(isAdminMode || isOperatorMode) && (
                                            <div className="mt-8 pt-6 border-t">
                                                <div className="flex justify-between items-center mb-4">
                                                    <h3 className="text-xl font-bold text-gray-800">{isAdminMode ? '👑 管理面板' : '🛠️ 區域營運商面板'}</h3>
                                                    {isAdminMode && (
                                                        <Link to="/inventory" className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded-lg transition">
                                                            📦 庫存管理
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {(isAdminMode || isOperatorMode) && <AdminPanel 
                                            db={db} 
                                            vendors={filteredVendors} 
                                            markets={filteredMarkets} 
                                            announcements={announcements} 
                                            pushNotifications={pushNotifications} 
                                            setConfirmation={setConfirmation} 
                                            setResetPasswordModal={setResetPasswordModal} 
                                            appId={appId} 
                                            functions={functions} 
                                            bookings={filteredBookings} 
                                            currentUser={currentUser} 
                                            handleDeletePhoto={handleDeletePhoto} 
                                            vendorPhotos={filteredVendorPhotos} 
                                            isAdminMode={isAdminMode} 
                                            isOperatorMode={isOperatorMode} 
                                            photoViewModal={photoViewModal}
                                            setPhotoViewModal={setPhotoViewModal}
                                        />}
                                    </>
                                )}
                            </div>
                        </div>
                        {loginModal.isOpen && <LoginModal onClose={() => setLoginModal({ isOpen: false })} vendors={vendors} onLoginSuccess={handleLoginSuccess} db={db} appId={appId}/>}
                        {accountModal.isOpen && currentUser && <AccountModal onClose={() => setAccountModal({ isOpen: false })} currentUser={currentUser} db={db} appId={appId} onResetPush={handleResetPush} />}
                        {resetPasswordModal.isOpen && <ResetPasswordModal config={resetPasswordModal} onClose={() => setResetPasswordModal({ isOpen: false, vendor: null })} db={db} appId={appId}/>}
                        {dayDetail.isOpen && <DayDetailModal detail={dayDetail} onClose={() => setDayDetail({ isOpen: false, date: null })} bookings={bookings} vendors={vendors} currentUser={currentUser} onAddBooking={openBookingModal} onDeleteBooking={handleDeleteBooking} setGeminiModal={setGeminiModal} db={db} appId={appId} functions={functions} markets={markets} setWeatherModal={setWeatherModal} onOpenCamera={handleOpenCamera} vendorPhotos={vendorPhotos} setPhotoPreviewModal={setPhotoPreviewModal} handleDeletePhoto={handleDeletePhoto}/>}
                        {bookingModal.isOpen && <BookingModal config={bookingModal} onClose={() => setBookingModal({isOpen: false, date:null, booking:null})} currentUser={currentUser} allBookings={bookings} markets={markets} db={db} setConfirmation={setConfirmation} appId={appId} functions={functions}/>}
                        {photoViewModal.isOpen && <PhotoViewModal config={photoViewModal} onClose={() => setPhotoViewModal({ isOpen: false, photo: null })} onDelete={handleDeletePhoto} />}
                        {photoPreviewModal && <PhotoPreviewModal config={photoPreviewModal} onClose={() => setPhotoPreviewModal({ isOpen: false, photo: null })} />}
                        <ConfirmationModal config={confirmation} onClose={() => setConfirmation({ ...confirmation, isOpen: false })} />
                        <GeminiModal config={geminiModal} onClose={() => setGeminiModal({ ...geminiModal, isOpen: false })} />
                        <SearchResultModal config={searchResultModal} onClose={() => setSearchResultModal({ isOpen: false, data: null })} />
                        <WeatherModal config={weatherModal} onClose={() => setWeatherModal({ isOpen: false, city: '', date: '', marketName: '' })} functions={functions} />

                        {installPrompt.isVisible && (
                            <PWAInstallPrompt 
                                onInstall={handleInstallPWA}
                                onClose={handleInstallClose}
                            />
                        )}

                        {/* 【AI修改】拍照留底彈窗 - 移至夥伴日期登記內容中 */}
                        {cameraModal.isOpen && (
                            <CameraModal 
                                config={cameraModal}
                                onCapture={handleCapturePhoto}
                                onClose={handleCloseCamera}
                            />
                        )}
                    </>
                } />
                <Route path="/inventory" element={
                    isLoading ? (
                        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                            <div className="text-center">
                                <h2 className="text-xl font-bold text-gray-700">載入中...</h2>
                                <p className="text-gray-600 mt-2">正在檢查權限，請稍候</p>
                            </div>
                        </div>
                    ) : isAdminMode ? (
                        <Suspense fallback={
                            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                                <div className="text-center">
                                    <h2 className="text-xl font-bold text-gray-700">載入庫存管理...</h2>
                                </div>
                            </div>
                        }>
                            <InventoryPage
                                currentUser={currentUser}
                                db={db}
                                appId={appId}
                                vendors={vendors}
                            />
                        </Suspense>
                    ) : currentUser ? (
                        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                            <div className="text-center">
                                <h2 className="text-xl font-bold text-red-600">權限不足</h2>
                                <p className="text-gray-600 mt-2">只有管理員可以訪問庫存管理系統</p>
                                <Link to="/" className="mt-4 inline-block bg-blue-500 text-white px-4 py-2 rounded">返回主頁</Link>
                            </div>
                        </div>
                    ) : (
                        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                            <div className="text-center">
                                <h2 className="text-xl font-bold text-blue-600">需要登入</h2>
                                <p className="text-gray-600 mt-2">請先登入才能訪問庫存管理系統</p>
                                <Link to="/" className="mt-4 inline-block bg-blue-500 text-white px-4 py-2 rounded">前往登入</Link>
                            </div>
                        </div>
                    )
                } />
            </Routes>
        </Router>
    );
};

// --- 子元件 ---







const DayDetailModal = ({ detail, onClose, bookings, vendors, currentUser, onAddBooking, onDeleteBooking, setGeminiModal, db, appId, functions, markets, setWeatherModal, onOpenCamera, vendorPhotos, setPhotoPreviewModal, handleDeletePhoto }) => { 
    // 【AI新增】根據夥伴區域權限篩選日期詳情的預約
    // Why: 確保夥伴只能看到有權限的區域預約詳情
    const dayBookings = useMemo(() => {
        if (!detail.isOpen) return [];
        const dateBookings = bookings.filter(b => b.date === detail.date);

        // 【AI修正】權限過濾防呆：
        // Why: 避免因資料異常（如市場無 region、夥伴無 regions）導致全部預約都看不見
        // 1. 沒有 currentUser 或 regions 欄位異常，顯示所有預約（向下相容）
        if (!currentUser || !Array.isArray(currentUser.regions) || currentUser.regions.length === 0) {
            return dateBookings.sort((a, b) => a.marketName.localeCompare(b.marketName));
        }

        // 2. 有權限設定才過濾，找不到市場/區域時預設顯示（防呆）
        return dateBookings.filter(booking => {
            const market = markets?.find(m => m.id === booking.marketId);
            if (!market) return true; // 找不到市場，預設顯示
            const marketRegion = getMarketRegion(market);
            if (!marketRegion) return true; // 找不到區域，預設顯示
            return currentUser.regions.includes(marketRegion);
        }).sort((a, b) => a.marketName.localeCompare(b.marketName));
    }, [bookings, detail.date, currentUser, markets, detail.isOpen]);
    
    if (!detail.isOpen) return null;
    const vendorMap = new Map(vendors.map(v => [v.id, v.name])); 
    
    // 【AI新增】根據市場ID找到對應的縣市
    // Why: 避免捉錯天氣資料，確保根據市場正確對應到縣市
    const getMarketCity = (marketId) => {
        const market = markets.find(m => m.id === marketId);
        return market ? market.city : null;
    };
    
    const handleGeneratePromoText = (booking) => { 
        const prompt = `請為「童顏家」產生一篇熱情有活力的社群媒體宣傳短文，用於宣傳擺攤活動。- 活動日期: ${booking.date} - 活動地點: ${booking.marketCity} ${booking.marketName} - 品牌與產品: 童顏家，專注於最新潮流的女鞋、女裝及時尚配件。- 風格要求: 親切、活潑、吸引人，結尾要包含行動呼籲 (例如：快來找我們尋寶！)。- 重要: 文案中請不要提及任何夥伴個人姓名。- 請使用繁體中文，並適度加入生動的表情符號 (emoji)。`; 
        callGeminiAPI(prompt, setGeminiModal, functions); 
    };
    
    // 【AI新增】處理天氣按鈕點擊
    // Why: 開啟天氣彈窗並傳入正確的縣市和日期資訊
    const handleWeatherClick = (booking) => {
        const city = getMarketCity(booking.marketId) || booking.marketCity;
        if (city) {
            setWeatherModal({
                isOpen: true,
                city: city,
                date: booking.date,
                marketName: booking.marketName
            });
        } else {
            alert('無法取得市場所在縣市資訊');
        }
    }; 
    return ( 
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-40 p-4" onClick={onClose}> 
            <div className="bg-gray-900 text-white w-full max-w-md mx-auto rounded-xl flex flex-col p-4" style={{maxHeight: '90vh'}} onClick={e => e.stopPropagation()}> 
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h3 className="text-xl font-bold">{detail.date}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white text-3xl">&times;</button>
                </div> 
                <div className="flex-grow overflow-y-auto space-y-3">
                    {dayBookings.length > 0 ? dayBookings.map(b => ( 
                        <div key={b.id} className="p-3 bg-gray-800 rounded-lg"> 
                            <div className="flex justify-between items-center"> 
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="font-bold text-lg">{b.marketName}</p>
                                        <button 
                                            onClick={() => handleWeatherClick(b)}
                                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold py-1 px-2 rounded-md transition-colors"
                                            title={`查看 ${b.marketName} 的天氣預報`}
                                        >
                                            🌤️ 天氣
                                        </button>
                                    </div>
                                    {currentUser && b.remark && <p className="text-sm text-yellow-400 mt-1">備註: {b.remark}</p>}
                                </div>
                                <div className="flex items-center gap-2">
                                    {currentUser && <p className="text-sm text-gray-400">{vendorMap.get(b.vendorId) || '未知'}</p>}
                                    {(currentUser?.id === b.vendorId || currentUser?.isAdmin) && <button onClick={() => onDeleteBooking(b)} className="bg-red-500 text-white text-xs font-semibold py-1 px-2 rounded-md hover:bg-red-600">刪除</button>}
                                </div>
                            </div> 
                            {(currentUser?.id === b.vendorId || currentUser?.isAdmin) && (
                                <div className="mt-2 space-y-2"> 
                                    <SalesInput booking={b} db={db} appId={appId} onSaveSuccess={onClose} currentUser={currentUser} />

                                    {/* 【AI新增】拍照按鈕 - 只有當天日期才能拍照 */}
                                    {(() => {
                                        // 檢查日期是否為今天
                                        const today = new Date();
                                        const bookingDate = new Date(b.date);
                                        const isToday = 
                                            bookingDate.getFullYear() === today.getFullYear() &&
                                            bookingDate.getMonth() === today.getMonth() &&
                                            bookingDate.getDate() === today.getDate();
                                        
                                        if (isToday) {
                                            return (
                                                <button 
                                                    onClick={() => {
                                                        const bookingInfo = {
                                                            date: b.date,
                                                            marketName: b.marketName,
                                                            marketCity: b.marketCity
                                                        };
                                                        onOpenCamera(bookingInfo);
                                                    }}
                                                    className="w-full text-sm bg-green-500 hover:bg-green-600 text-white font-semibold py-1 px-2 rounded-md"
                                                >
                                                    📸 拍攝攤位照
                                                </button>
                                            );
                                        } else {
                                            const isPast = bookingDate < today;
                                            return (
                                                <button 
                                                    disabled
                                                    className="w-full text-sm bg-gray-400 text-gray-600 font-semibold py-1 px-2 rounded-md cursor-not-allowed"
                                                    title={isPast ? "過去日期無法拍照" : "未來日期無法拍照"}
                                                >
                                                    📸 拍攝攤位照 {isPast ? "(已過期)" : "(未到)"}
                                                </button>
                                            );
                                        }
                                    })()}

                                    <button onClick={() => handleGeneratePromoText(b)} className="w-full text-sm bg-purple-500 hover:bg-purple-600 text-white font-semibold py-1 px-2 rounded-md">✨ 產生宣傳文案</button> 
                                </div>
                            )}

                            {/* 【AI新增】當日照片預覽 - 只有登入使用者才能看到 */}
                            {currentUser && (() => {
                                const dayPhotos = vendorPhotos.filter(photo => 
                                    photo.bookingInfo && photo.bookingInfo.date === detail.date && 
                                    photo.bookingInfo.marketName === b.marketName
                                );
                                
                                if (dayPhotos.length > 0) {
                                    return (
                                        <div className="mt-3">
                                            <h4 className="text-sm font-medium text-gray-300 mb-2">📸 當日照片</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {dayPhotos.map((photo, index) => (
                                                    <div 
                                                        key={photo.id || index}
                                                        className="relative cursor-pointer group"
                                                    >
                                                        <img 
                                                            src={photo.photoDataUrl} 
                                                            alt={`${photo.vendorName} 拍攝的照片`}
                                                            className="w-16 h-16 object-cover rounded-lg border-2 border-gray-600 hover:border-blue-400 transition-colors"
                                                            onClick={() => setPhotoPreviewModal({ isOpen: true, photo })}
                                                        />
                                                        {/* 【AI修正】照片刪除按鈕 - 只有當天照片的拍攝者和管理員可以刪除 */}
                                                        {(() => {
                                                            // 檢查照片拍攝日期是否為今天
                                                            const photoDate = photo.timestamp ? new Date(photo.timestamp) : null;
                                                            const today = new Date();
                                                            const isToday = photoDate && 
                                                                photoDate.getFullYear() === today.getFullYear() &&
                                                                photoDate.getMonth() === today.getMonth() &&
                                                                photoDate.getDate() === today.getDate();
                                                            
                                                            // 只有當天照片的拍攝者和管理員可以刪除
                                                            // 【AI修正】夥伴只能刪除自己的照片，管理員可以刪除所有當天照片
                                                            const canDelete = isToday && (
                                                                (currentUser?.isAdmin) || 
                                                                (currentUser?.id === photo.vendorId)
                                                            );
                                                            
                                                            return canDelete ? (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeletePhoto(photo);
                                                                    }}
                                                                    className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center transition-colors"
                                                                    title="刪除照片"
                                                                >
                                                                    ×
                                                                </button>
                                                            ) : null;
                                                        })()}
                                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-lg transition-all flex items-center justify-center pointer-events-none">
                                                            <span className="text-white text-xs opacity-0 group-hover:opacity-100">點擊放大</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })()} 
                        </div> 
                    )) : <p className="text-gray-400 text-center py-10">本日尚無登記。</p>}
                </div> 
                {currentUser && <button onClick={() => onAddBooking(detail.date)} className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-lg mt-4 flex-shrink-0">新增此日登記</button>} 
            </div> 
        </div> 
    ); 
};
const AdminPanel = ({ db, vendors, markets, announcements, pushNotifications, setConfirmation, setResetPasswordModal, appId, functions, bookings, currentUser, handleDeletePhoto, vendorPhotos, photoViewModal, setPhotoViewModal, isLoadingPhotos, loadVendorPhotos, isAdminMode, isOperatorMode }) => {
    // 【AI新增】管理員幫夥伴登記狀態 - 移至最上方
    // Why: 提供管理員快速為夥伴新增擺攤登記的功能，包含重複排班和備註
    const [selectedVendorForBooking, setSelectedVendorForBooking] = useState('');
    const [selectedDateForBooking, setSelectedDateForBooking] = useState('');
    const [selectedMarketForBooking, setSelectedMarketForBooking] = useState('');
    const [selectedCityForBooking, setSelectedCityForBooking] = useState(TAIWAN_CITIES[0]);
    
    // 【AI新增】當選擇的夥伴改變時，自動調整縣市選擇
    // Why: 確保選擇的縣市符合夥伴的區域權限
    useEffect(() => {
        if (selectedVendorForBooking) {
            const selectedVendor = vendors.find(v => v.id === selectedVendorForBooking);
            if (selectedVendor && selectedVendor.regions && selectedVendor.regions.length > 0) {
                const availableCities = TAIWAN_CITIES.filter(city => {
                    const cityRegion = getRegionByCity(city);
                    return selectedVendor.regions.includes(cityRegion);
                });
                
                if (availableCities.length > 0 && !availableCities.includes(selectedCityForBooking)) {
                    setSelectedCityForBooking(availableCities[0]);
                    setSelectedMarketForBooking('');
                }
            }
        }
    }, [selectedVendorForBooking, vendors, selectedCityForBooking]);
    const [isAddingMarketForBooking, setIsAddingMarketForBooking] = useState(false);
    const [newMarketForBooking, setNewMarketForBooking] = useState({ city: TAIWAN_CITIES[0], name: '' });
    const [bookingError, setBookingError] = useState('');
    const [isSavingBooking, setIsSavingBooking] = useState(false);
    // 【AI修正】重複排班和備註功能 - 與主功能保持一致
    const [repeatForBooking, setRepeatForBooking] = useState('none');
    const [remarkForBooking, setRemarkForBooking] = useState('');

    const [newVendorId, setNewVendorId] = useState('');
    const [newVendorName, setNewVendorName] = useState('');
    const [newVendorPassword, setNewVendorPassword] = useState('');
    const [isNewVendorAdmin, setIsNewVendorAdmin] = useState(false);
    // 【AI新增】夥伴角色狀態管理 - 2025/10/02 V1.02
    // Why: 管理新夥伴的角色設定（admin/operator/partner），解決 role 欄位 undefined 錯誤
    const [newVendorRole, setNewVendorRole] = useState('partner'); // 預設為夥伴角色
    // 【AI新增】夥伴區域權限狀態管理
    // Why: 管理新夥伴的區域權限設定
    const [newVendorRegions, setNewVendorRegions] = useState([...ALL_REGIONS]); // 預設全區權限
    const [vendorError, setVendorError] = useState('');
    const [newMarketCity, setNewMarketCity] = useState(TAIWAN_CITIES[0]);
    const [newMarketName, setNewMarketName] = useState('');
    const [newMarketLatitude, setNewMarketLatitude] = useState('');
    const [newMarketLongitude, setNewMarketLongitude] = useState('');
    const [marketError, setMarketError] = useState('');
    // 【AI新增】市場區域篩選狀態管理
    // Why: 提供市場列表的區域篩選功能
    const [marketRegionFilter, setMarketRegionFilter] = useState('');
    const [marketCityFilter, setMarketCityFilter] = useState('');
    const [newAnnouncement, setNewAnnouncement] = useState('');
    const [announcementError, setAnnouncementError] = useState('');
    const [newPushTitle, setNewPushTitle] = useState('');
    const [newPushBody, setNewPushBody] = useState('');
    const [pushTargetType, setPushTargetType] = useState('all');
    const [pushError, setPushError] = useState('');
    const [editingMarket, setEditingMarket] = useState(null);
    const [editingVendor, setEditingVendor] = useState(null);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    // 【AI新增】天氣通知管理狀態
    // Why: 提供管理員手動觸發天氣檢查和查看天氣通知狀態的功能
    const [isCheckingWeather, setIsCheckingWeather] = useState(false);
    const [weatherCheckResult, setWeatherCheckResult] = useState('');
    
    // 【AI新增】天氣預載管理狀態
    // Why: 提供管理員手動觸發天氣資料預載的功能
    const [isPreloadingWeather, setIsPreloadingWeather] = useState(false);
    const [preloadResult, setPreloadResult] = useState('');
    
    // 【AI新增】照片管理狀態
    // Why: 提供管理員查看和管理夥伴拍照留底的功能
    // const [vendorPhotos, setVendorPhotos] = useState([]);
    // const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
    // const [selectedPhoto, setSelectedPhoto] = useState(null);
    // const [photoViewModal, setPhotoViewModal] = useState({ isOpen: false, photo: null });
    
    const vendorsColPath = `artifacts/${appId}/public/data/vendors`;
    const marketsColPath = `artifacts/${appId}/public/data/markets`;
    const announcementsColPath = `artifacts/${appId}/public/data/announcements`;
    const pushNotificationsColPath = `artifacts/${appId}/public/data/pushNotifications`;
    const bookingsColPath = `artifacts/${appId}/public/data/bookings`;
    const vendorPhotosColPath = `artifacts/${appId}/public/data/vendorPhotos`;

    // 【AI新增】管理員幫夥伴登記功能 - 支援重複排班和備註
    const handleAdminAddBooking = async (e) => {
        e.preventDefault();
        setBookingError('');
        if (!selectedVendorForBooking || !selectedDateForBooking || !selectedMarketForBooking) {
            return setBookingError('請選擇夥伴、日期和市場！');
        }
        setIsSavingBooking(true);
        try {
            const vendor = vendors.find(v => v.id === selectedVendorForBooking);
            const market = markets.find(m => m.id === selectedMarketForBooking);
            const marketDetails = markets.find(m => m.id === selectedMarketForBooking);
            
            const batch = writeBatch(db);
            
            // 【AI修正】重複排班邏輯 - 與主功能保持一致
            const recurringId = repeatForBooking !== 'none' ? crypto.randomUUID() : undefined;
            let bookingDates = [];
            if (repeatForBooking === 'none') {
                bookingDates = [new Date(selectedDateForBooking)];
            } else {
                bookingDates = generateRecurringDatesForBooking(selectedDateForBooking, repeatForBooking);
            }
            
            for (let d of bookingDates) {
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const finalDate = `${year}-${month}-${day}`;
                
                // 首頁只載入當月；新增前只查目標日期，避免跨月重複登記。
                const sameDateSnapshot = await getDocs(query(
                    collection(db, bookingsColPath),
                    where('date', '==', finalDate)
                ));
                const existingBooking = sameDateSnapshot.docs
                    .map(item => item.data())
                    .find(item => item.vendorId === selectedVendorForBooking);
                if (existingBooking) {
                    setBookingError(`${vendor?.name} 在 ${finalDate} 已有登記！`);
                    setIsSavingBooking(false);
                    return;
                }
                
                const bookingData = {
                    vendorId: selectedVendorForBooking,
                    vendorName: vendor?.name || '未知',
                    marketId: selectedMarketForBooking,
                    marketName: marketDetails?.name || '未知',
                    marketCity: marketDetails?.city || '未知',
                    date: finalDate,
                    remark: remarkForBooking.trim(),
                    createdAt: serverTimestamp(),
                    createdBy: 'admin',
                    ...(recurringId ? { recurringId } : {})
                };
                
                const docRef = doc(collection(db, bookingsColPath));
                batch.set(docRef, bookingData);
            }
            
            await batch.commit();
            
            // 【AI新增】管理員幫夥伴登記成功後發送推播通知
            // Why: 讓夥伴知道管理員已為其安排擺攤，提升溝通效率
            try {
                const sendPushNotification = httpsCallable(functions, 'sendPushNotification');
                // 格式化日期為 月/日
                const formatDate = (dateString) => {
                    const date = new Date(dateString);
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${month}/${day}`;
                };
                
                await sendPushNotification({
                    appId,
                    title: `"${currentUser?.name || '管理員'}" 為 "${vendor?.name}"`,
                    body: `"${formatDate(selectedDateForBooking)}" - "${marketDetails?.name}"`,
                    targetType: 'all'
                });
                
                console.log('✅ 推播通知已發送給夥伴');
            } catch (pushError) {
                console.error('❌ 推播通知發送失敗:', pushError);
                // 推播失敗不影響主要功能，只記錄錯誤
            }
            
            // 清空表單
            setSelectedVendorForBooking('');
            setSelectedDateForBooking('');
            setSelectedMarketForBooking('');
            setSelectedCityForBooking(TAIWAN_CITIES[0]);
            setRepeatForBooking('none');
            setRemarkForBooking('');
            setBookingError('');
            
            const repeatText = repeatForBooking === 'none' ? '' : `（重複排班）`;
            alert(`✅ 已成功為 ${vendor?.name} 新增 ${selectedDateForBooking} 的擺攤登記${repeatText}！\n📱 推播通知已發送給夥伴。`);
        } catch (err) {
            setBookingError('新增登記失敗：' + err.message);
        } finally {
            setIsSavingBooking(false);
        }
    };

    // 【AI修正】重複規則選單選項 - 與主功能保持一致
    // Why: 讓管理員幫夥伴登記的重複排班選單與主功能保持一致
    const getRepeatOptionsForBooking = (selectedDate) => {
        const date = new Date(selectedDate);
        const weekday = date.getDay(); // 0(日)~6(六)
        const dayOfMonth = date.getDate(); // 1-31
        const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
        const weekdayName = weekdays[weekday];
        
        return [
            { value: 'none', label: '無重複' },
            { value: 'daily', label: '每天' },
            { value: `weekly_${weekday}`, label: `每週星期${weekdayName}` },
            { value: `biweekly_${weekday}`, label: `每隔週星期${weekdayName}` },
            { value: `monthly_first_${weekday}`, label: `每月第1星期${weekdayName}` },
            { value: `monthly_${dayOfMonth}`, label: `每月${dayOfMonth}日` },
        ];
    };

    // 【AI修正】產生重複日期的工具函數 - 與主功能保持一致
    // Why: 讓管理員幫夥伴登記的重複排班邏輯與主功能保持一致
    function generateRecurringDatesForBooking(startDate, repeat) {
        const dates = [];
        let date = new Date(startDate);
        const endDate = new Date(`${date.getFullYear()}-12-31`);
        const targetWeekday = date.getDay(); // 0(日)~6(六)
        const targetDayOfMonth = date.getDate(); // 1-31
        
        while (date <= endDate) {
            if (repeat === 'none') break;
            // 推入當前日期
            dates.push(new Date(date));
            // 計算下一個日期
            switch (repeat) {
                case 'daily':
                    date.setDate(date.getDate() + 1);
                    break;
                case `weekly_${targetWeekday}`: // 每週同星期幾
                    date.setDate(date.getDate() + 7);
                    break;
                case `biweekly_${targetWeekday}`: // 每隔週同星期幾
                    date.setDate(date.getDate() + 14);
                    break;
                case `monthly_first_${targetWeekday}`: {
                    let y = date.getFullYear();
                    let m = date.getMonth() + 1;
                    if (m > 11) { y++; m = 0; }
                    let d = new Date(y, m, 1);
                    while (d.getDay() !== targetWeekday) d.setDate(d.getDate() + 1);
                    date = d;
                    break;
                }
                case `monthly_${targetDayOfMonth}`: {
                    let y = date.getFullYear();
                    let m = date.getMonth() + 1;
                    if (m > 11) { y++; m = 0; }
                    date = new Date(y, m, targetDayOfMonth);
                    break;
                }
                case 'yearly': {
                    date.setFullYear(date.getFullYear() + 1);
                    break;
                }
                default:
                    break;
            }
        }
        return dates;
    }

    // 【AI新增】管理員幫夥伴登記 - 新增市場功能
    const handleAddMarketForBooking = async () => {
        if (!newMarketForBooking.city || !newMarketForBooking.name.trim()) {
            return setBookingError('請選擇縣市並輸入市場名稱！');
        }
        try {
            const marketRegion = getRegionByCity(newMarketForBooking.city);
            if (!marketRegion) {
                return setBookingError(`無法判斷「${newMarketForBooking.city}」的所屬區域！`);
            }
            const marketData = {
                city: newMarketForBooking.city,
                name: newMarketForBooking.name.trim(),
                region: marketRegion,
                regionUpdatedAt: serverTimestamp()
            };
            const result = await createUniqueMarket({ db, appId, markets, marketData });
            if (!result.created) {
                setSelectedMarketForBooking(result.market.id);
                return setBookingError(`「${result.market.name}」已存在於${result.market.city}，不能重複新增。`);
            }
            // 補上操作紀錄
            await logMarketOperation(db, appId, 'add', result.market, currentUser);
            setSelectedMarketForBooking(result.market.id);
            setIsAddingMarketForBooking(false);
            setNewMarketForBooking({ city: TAIWAN_CITIES[0], name: '' });
            setBookingError('');
        } catch (err) {
            setBookingError('新增市場失敗：' + err.message);
        }
    };

    // 【AI新增】管理員功能函數
    // Why: 提供管理員管理夥伴、市場、公告和推播通知的功能
    const handleAddVendor = async (e) => { 
        e.preventDefault(); 
        setVendorError(''); 
        if (!newVendorId || !newVendorName || !newVendorPassword) { 
            return setVendorError('編號、名稱和密碼不可為空！'); 
        } 
        if (vendors.some(v => v.id.toLowerCase() === newVendorId.toLowerCase())) { 
            return setVendorError('此編號已存在！'); 
        } 
        // 【AI新增】區域權限驗證
        // Why: 確保夥伴至少有一個區域權限
        if (!newVendorRegions || newVendorRegions.length === 0) {
            return setVendorError('請至少選擇一個區域權限！');
        }
        if (!isValidRegionsArray(newVendorRegions)) {
            return setVendorError('區域權限設定無效！');
        }
        try { 
            await setDoc(doc(db, vendorsColPath, newVendorId), { 
                name: newVendorName, 
                isAdmin: isNewVendorAdmin, 
                password: newVendorPassword, 
                isActive: true,
                // 寫入角色欄位，支援 operator
                role: newVendorRole,
                // 【AI新增】區域權限欄位
                regions: [...newVendorRegions], // 複製陣列避免引用問題
                regionUpdatedAt: serverTimestamp(),
                regionUpdatedBy: currentUser?.id || 'admin'
            }); 
            setNewVendorId(''); 
            setNewVendorName(''); 
            setNewVendorPassword(''); 
            setIsNewVendorAdmin(false); 
            setNewVendorRole('partner'); // 新增完重置角色
            setNewVendorRegions([...ALL_REGIONS]);
        } catch (err) { 
            setVendorError('新增失敗：' + err.message); 
        } 
    };
    
    const handleToggleVendorStatus = async (vendor) => { 
        const newStatus = !vendor.isActive;
        const actionText = newStatus ? '啟用' : '停用';
        setConfirmation({ 
            isOpen: true, 
            title: `${actionText}夥伴`, 
            message: `確定要${actionText}「${vendor.name} (${vendor.id})」嗎？${!newStatus ? '\n⚠️ 停用後該夥伴將無法登入，但歷史資料會保留。' : ''}`, 
            onConfirm: async () => { 
                try { 
                    const vendorRef = doc(db, vendorsColPath, vendor.id); 
                    await updateDoc(vendorRef, { isActive: newStatus }); 
                } catch (err) { 
                    alert(`${actionText}失敗：` + err.message); 
                } 
            } 
        }); 
    };
    
    const handleDeleteVendor = async (vendorId) => { 
        const vendor = vendors.find(v => v.id === vendorId);
        let vendorBookings = [];
        try {
            // 危險操作的歷史資訊改為按需讀取，首頁不需為此下載所有預約。
            const vendorBookingsSnapshot = await getDocs(query(
                collection(db, bookingsColPath),
                where('vendorId', '==', vendorId)
            ));
            vendorBookings = vendorBookingsSnapshot.docs.map(item => item.data());
        } catch (err) {
            console.error('讀取夥伴歷史預約失敗:', err);
            return alert('無法確認夥伴歷史預約，為保護資料已停止刪除。');
        }
        const lastBooking = vendorBookings
            .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
        const daysSinceLastBooking = lastBooking ? 
            Math.floor((new Date() - new Date(lastBooking.date)) / (1000 * 60 * 60 * 24)) : '從未';
            
        setConfirmation({ 
            isOpen: true, 
            title: '⚠️ 危險操作：永久刪除夥伴', 
            message: `您確定要永久刪除「${vendor?.name} (${vendorId})」嗎？\n\n📊 夥伴資訊：\n• 帳號狀態：已停用\n• 最後使用：${daysSinceLastBooking === '從未' ? '從未使用' : `${daysSinceLastBooking}天前`}\n• 預訂記錄：${vendorBookings.length}筆\n\n⚠️ 警告：此操作會導致所有歷史預訂記錄中的夥伴名稱顯示為「未知」！\n\n💡 建議：如果不確定，可以繼續保持停用狀態。`,
            onConfirm: async () => { 
                try { 
                    await deleteDoc(doc(db, vendorsColPath, vendorId)); 
                } catch (err) { 
                    alert('刪除失敗：' + err.message); 
                } 
            } 
        }); 
    };
    
    const handleUpdateVendor = async () => { 
        if (!editingVendor || !editingVendor.name) { 
            return alert('夥伴名稱不可為空！'); 
        } 
        // 【AI新增】區域權限驗證
        // Why: 確保編輯時的區域權限設定有效
        if (!editingVendor.regions || editingVendor.regions.length === 0) {
            return alert('請至少選擇一個區域權限！');
        }
        if (!isValidRegionsArray(editingVendor.regions)) {
            return alert('區域權限設定無效！');
        }
        try { 
            const vendorRef = doc(db, vendorsColPath, editingVendor.id); 
            // 【AI修正】確保 role 欄位有值 - 2025/10/02 V1.32.2
            // Why: 修復更新夥伴時 role 欄位 undefined 錯誤，自動根據 isAdmin 設定預設值
            const roleValue = editingVendor.role || (editingVendor.isAdmin ? 'admin' : 'partner');
            await updateDoc(vendorRef, { 
                name: editingVendor.name, 
                isAdmin: editingVendor.isAdmin, 
                isActive: editingVendor.isActive !== false,
                // 寫入角色欄位，支援 operator
                role: roleValue,
                // 【AI新增】區域權限欄位更新
                regions: [...editingVendor.regions], // 複製陣列避免引用問題
                regionUpdatedAt: serverTimestamp(),
                regionUpdatedBy: currentUser?.id || 'admin'
            }); 
            setEditingVendor(null); 
        } catch (err) { 
            alert('更新夥伴失敗: ' + err.message); 
        } 
    };
    
    const handleAddNewMarket = async (e) => { 
        e.preventDefault(); 
        setMarketError(''); 
        if (!newMarketCity || !newMarketName) { 
            return setMarketError('縣市和市場名稱不可為空！'); 
        } 
        try { 
            // 【AI新增】自動判斷市場區域
            const marketRegion = getRegionByCity(newMarketCity);
            if (!marketRegion) {
                return setMarketError(`無法判斷「${newMarketCity}」的所屬區域！`);
            }
            const marketData = { 
                city: newMarketCity, 
                name: newMarketName.trim(),
                region: marketRegion,
                regionUpdatedAt: serverTimestamp()
            };
            if (newMarketLatitude && newMarketLongitude) {
                marketData.latitude = parseFloat(newMarketLatitude);
                marketData.longitude = parseFloat(newMarketLongitude);
            }
            const result = await createUniqueMarket({ db, appId, markets, marketData });
            if (!result.created) {
                return setMarketError(`「${result.market.name}」已存在於${result.market.city}，不能重複新增。`);
            }
            // 【AI承諾】新增市場時記錄操作紀錄，方便稽核
            await logMarketOperation(db, appId, 'add', result.market, currentUser);
            setNewMarketCity(TAIWAN_CITIES[0]); 
            setNewMarketName(''); 
            setNewMarketLatitude(''); 
            setNewMarketLongitude(''); 
        } catch (err) { 
            setMarketError('新增市場失敗: ' + err.message); 
        } 
    };
    
    const handleUpdateMarket = async () => { 
        if (!editingMarket || !editingMarket.city || !editingMarket.name) { 
            return alert('縣市和市場名稱不可為空！'); 
        } 
        try { 
            // 【AI新增】自動判斷或驗證市場區域
            // Why: 確保市場區域分類的正確性
            const marketRegion = getRegionByCity(editingMarket.city);
            if (!marketRegion) {
                return alert(`無法判斷「${editingMarket.city}」的所屬區域！`);
            }

            const duplicate = await findDuplicateMarketInFirestore(
                db,
                appId,
                editingMarket.city,
                editingMarket.name,
                editingMarket.id
            );
            if (duplicate) {
                return alert(`「${duplicate.name}」已存在於${duplicate.city}，不能儲存為重複市場。`);
            }
            
            const marketData = { 
                city: editingMarket.city, 
                name: editingMarket.name,
                // 【AI新增】區域分類欄位更新
                region: marketRegion,
                regionUpdatedAt: serverTimestamp()
            };
            
            // 【AI新增】如果提供了經緯度，則加入市場資料
            if (editingMarket.latitude && editingMarket.longitude) {
                marketData.latitude = parseFloat(editingMarket.latitude);
                marketData.longitude = parseFloat(editingMarket.longitude);
            }
            
            const marketRef = doc(db, marketsColPath, editingMarket.id); 
            await updateDoc(marketRef, marketData); 
            setEditingMarket(null); 
        } catch (err) { 
            alert('更新失敗: ' + err.message); 
        } 
    };
    
    const handleDeleteMarket = (market) => { 
        setConfirmation({ 
            isOpen: true, 
            title: '刪除市場', 
            message: `確定要刪除「${market.name}」嗎？`, 
            onConfirm: async () => { 
                try { 
                    await deleteDoc(doc(db, marketsColPath, market.id)); 
                    // 【AI承諾】刪除市場時記錄操作紀錄，方便稽核
                    await logMarketOperation(db, appId, 'delete', market, currentUser);
                } catch (err) { 
                    alert('刪除失敗: ' + err.message); 
                } 
            } 
        }); 
    };
    
    const handlePostAnnouncement = async (e) => { 
        e.preventDefault(); 
        setAnnouncementError(''); 
        if (!newAnnouncement.trim()) { 
            return setAnnouncementError('公告內容不可為空！'); 
        } 
        try { 
            await addDoc(collection(db, announcementsColPath), { 
                content: newAnnouncement, 
                createdAt: serverTimestamp() 
            }); 
            setNewAnnouncement(''); 
        } catch (err) { 
            setAnnouncementError('發布失敗: ' + err.message); 
        } 
    };
    
    const handleUpdateAnnouncement = async () => { 
        if (!editingAnnouncement || !editingAnnouncement.content.trim()) return; 
        try { 
            await updateDoc(doc(db, announcementsColPath, editingAnnouncement.id), { 
                content: editingAnnouncement.content 
            }); 
            setEditingAnnouncement(null); 
        } catch (err) { 
            alert('更新公告失敗: ' + err.message); 
        } 
    };
    
    const handleDeleteAnnouncement = (announcementId) => { 
        setConfirmation({ 
            isOpen: true, 
            title: '刪除公告', 
            message: '確定要刪除這則公告嗎？', 
            onConfirm: async () => { 
                try { 
                    await deleteDoc(doc(db, announcementsColPath, announcementId)); 
                } catch (err) { 
                    alert('刪除公告失敗: ' + err.message); 
                } 
            } 
        }); 
    };
    
    const handleSendPushNotification = async (e) => { 
        e.preventDefault(); 
        setPushError(''); 
        if (!newPushTitle.trim() || !newPushBody.trim()) { 
            return setPushError('標題和內容不可為空！'); 
        } 
        try { 
            const sendPushNotification = httpsCallable(functions, 'sendPushNotification'); 
            await sendPushNotification({ 
                appId, 
                title: newPushTitle.trim(), 
                body: newPushBody.trim(), 
                targetType: pushTargetType 
            }); 
            setNewPushTitle(''); 
            setNewPushBody(''); 
            setPushTargetType('all'); 
        } catch (err) { 
            setPushError('發送推播失敗: ' + err.message); 
        } 
    };
    
    const handleDeletePushNotification = (notificationId) => { 
        setConfirmation({ 
            isOpen: true, 
            title: '刪除推播記錄', 
            message: '確定要刪除這則推播記錄嗎？', 
            onConfirm: async () => { 
                try { 
                    await deleteDoc(doc(db, pushNotificationsColPath, notificationId)); 
                } catch (err) { 
                    alert('刪除推播記錄失敗: ' + err.message); 
                } 
            } 
        }); 
    };

    // 【AI新增】手動觸發天氣檢查功能
    // Why: 讓管理員可以立即檢查天氣並發送通知，不用等待定時任務
    const handleManualWeatherCheck = async () => {
        setIsCheckingWeather(true);
        setWeatherCheckResult('');
        
        try {
            console.log('🌤️ 管理員手動觸發天氣檢查...');
            
            const checkWeatherAndNotify = httpsCallable(functions, 'checkWeatherAndNotify');
            const result = await checkWeatherAndNotify({ appId });
            
            if (result.data.success) {
                const message = result.data.message;
                const details = result.data.weatherAlerts || [];
                
                let resultText = `✅ ${message}`;
                if (details.length > 0) {
                    resultText += `\n\n發現的惡劣天氣：`;
                    details.forEach(alert => {
                        resultText += `\n• ${alert.city} ${alert.date}: ${alert.description}`;
                    });
                    resultText += `\n\n已通知 ${result.data.affectedVendors} 位攤商（${result.data.successCount} 成功，${result.data.failureCount} 失敗）`;
                }
                
                setWeatherCheckResult(resultText);
            } else {
                setWeatherCheckResult(`❌ 天氣檢查失敗：${result.data.error}`);
            }
        } catch (err) {
            console.error('❌ 天氣檢查錯誤:', err);
            setWeatherCheckResult(`❌ 天氣檢查失敗：${err.message}`);
        } finally {
            setIsCheckingWeather(false);
        }
    };
    
    // 【AI新增】手動觸發天氣預載功能
    // Why: 讓管理員可以主動預載未來7天所有縣市的天氣資料
    const handlePreloadWeatherData = async () => {
        setIsPreloadingWeather(true);
        setPreloadResult('');
        
        try {
            console.log('📦 管理員手動觸發天氣資料預載...');
            
            const preloadWeatherData = httpsCallable(functions, 'preloadWeatherData');
            const result = await preloadWeatherData();
            
            if (result.data.success) {
                const stats = result.data.stats;
                let resultText = `✅ ${result.data.message}`;
                resultText += `\n\n📊 預載統計：`;
                resultText += `\n• 日期範圍：${stats.dateRange}`;
                resultText += `\n• 縣市數量：${stats.cities} 個`;
                resultText += `\n• 日期數量：${stats.dates} 天`;
                resultText += `\n• 總處理數：${stats.totalAttempts}`;
                resultText += `\n• 成功數量：${stats.successCount}`;
                resultText += `\n• 失敗數量：${stats.failureCount}`;
                resultText += `\n• 快取命中：${stats.cacheHitCount}`;
                resultText += `\n• API呼叫：${stats.apiCallCount}`;
                
                setPreloadResult(resultText);
            } else {
                setPreloadResult(`❌ 天氣預載失敗：${result.data.error}`);
            }
        } catch (err) {
            console.error('❌ 天氣預載錯誤:', err);
            setPreloadResult(`❌ 天氣預載失敗：${err.message}`);
        } finally {
            setIsPreloadingWeather(false);
        }
    };

    // 【AI新增】照片管理功能
    // Why: 提供管理員查看和管理夥伴拍照留底的功能
    // const loadVendorPhotos = async () => {
    //     setIsLoadingPhotos(true);
    //     try {
    //         const photosQuery = query(
    //             collection(db, vendorPhotosColPath),
    //             orderBy('capturedAt', 'desc'),
    //             limit(50)
    //         );
    //         const photosSnapshot = await getDocs(photosQuery);
    //         const photosData = photosSnapshot.docs.map(doc => ({
    //             id: doc.id,
    //             ...doc.data()
    //         }));
    //         setVendorPhotos(photosData);
    //     } catch (error) {
    //         console.error('載入照片失敗:', error);
    //     } finally {
    //         setIsLoadingPhotos(false);
    //     }
    // };

    const handleViewPhoto = (photo) => {
        setPhotoViewModal({ isOpen: true, photo });
    };

    return (
        <div className="mt-4">
            {/* <h3 className="text-xl font-bold text-gray-800 mb-4">👑 管理面板</h3> */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-6">
                {/* 【AI新增】幫夥伴登記區塊 - 管理員與營運商都可見 */}
                <details className="space-y-3" open>
                    <summary className="font-semibold cursor-pointer">📅 幫夥伴登記</summary>
                    <form onSubmit={handleAdminAddBooking} className="space-y-3 bg-white p-3 rounded-md border">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">1. 選擇夥伴</label>
                            <select value={selectedVendorForBooking} onChange={e => setSelectedVendorForBooking(e.target.value)} className="w-full p-2 border rounded-lg text-gray-700 bg-white" required>
                                <option value="">請選擇夥伴...</option>
                                {vendors.filter(v => v.isActive !== false).map(vendor => (
                                    <option key={vendor.id} value={vendor.id}>{vendor.name} ({vendor.id})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">2. 選擇日期</label>
                            <input type="date" value={selectedDateForBooking} onChange={e => setSelectedDateForBooking(e.target.value)} className="w-full p-2 border rounded-lg text-gray-700 bg-white" min={new Date().toISOString().split('T')[0]} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">3. 選擇市場</label>
                            <div className="flex items-center gap-2">
                                <select value={selectedCityForBooking} onChange={e => { setSelectedCityForBooking(e.target.value); setSelectedMarketForBooking(''); }} className="w-1/3 p-2 border rounded-lg text-gray-700 bg-white">
                                    {(() => {
                                        // 【AI新增】管理員幫夥伴登記時的縣市篩選
                                        // Why: 根據選擇的夥伴權限篩選可選擇的縣市
                                        if (selectedVendorForBooking) {
                                            const selectedVendor = vendors.find(v => v.id === selectedVendorForBooking);
                                            if (selectedVendor && selectedVendor.regions && selectedVendor.regions.length > 0) {
                                                return TAIWAN_CITIES.filter(city => {
                                                    const cityRegion = getRegionByCity(city);
                                                    return selectedVendor.regions.includes(cityRegion);
                                                });
                                            }
                                        }
                                        
                                        return TAIWAN_CITIES;
                                    })().map(city => (<option key={city} value={city}>{city}</option>))}
                                </select>
                                <select value={selectedMarketForBooking} onChange={e => setSelectedMarketForBooking(e.target.value)} className="w-2/3 p-2 border rounded-lg text-gray-700 bg-white" required>
                                    <option value="">請選擇市場...</option>
                                    {(() => {
                                        // 【AI新增】管理員幫夥伴登記時的市場篩選
                                        // Why: 根據選擇的夥伴權限篩選可登記的市場
                                        const cityFiltered = markets.filter(m => m.city === selectedCityForBooking);
                                        
                                        if (selectedVendorForBooking) {
                                            const selectedVendor = vendors.find(v => v.id === selectedVendorForBooking);
                                            if (selectedVendor && selectedVendor.regions && selectedVendor.regions.length > 0) {
                                                return cityFiltered.filter(market => {
                                                    const marketRegion = getMarketRegion(market);
                                                    return selectedVendor.regions.includes(marketRegion);
                                                });
                                            }
                                        }
                                        
                                        return cityFiltered;
                                    })().map(market => (
                                        <option key={market.id} value={market.id}>{market.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="text-right mt-1">
                                <button type="button" onClick={() => setIsAddingMarketForBooking(!isAddingMarketForBooking)} className="text-sm text-blue-500 hover:underline">
                                    {isAddingMarketForBooking ? '取消新增' : '找不到市場？點此新增'}
                                </button>
                            </div>
                        </div>
                        {isAddingMarketForBooking && (
                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <h4 className="font-bold text-blue-800 mb-2">➕ 新增市場</h4>
                                <div className="space-y-2">
                                    <select value={newMarketForBooking.city} onChange={e => setNewMarketForBooking({ ...newMarketForBooking, city: e.target.value })} className="w-full p-2 border border-blue-300 rounded">
                                        {TAIWAN_CITIES.map(city => (<option key={city} value={city}>{city}</option>))}
                                    </select>
                                    <input type="text" value={newMarketForBooking.name} onChange={e => setNewMarketForBooking({ ...newMarketForBooking, name: e.target.value })} placeholder="市場名稱" className="w-full p-2 border border-blue-300 rounded" />
                                    <button type="button" onClick={handleAddMarketForBooking} className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600">新增市場</button>
                                </div>
                            </div>
                        )}
                        {/* 【AI修正】重複排班功能 - 與主功能保持一致 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">4. 重複排班</label>
                            <select 
                                value={repeatForBooking} 
                                onChange={e => setRepeatForBooking(e.target.value)} 
                                className="w-full p-2 border rounded-lg text-gray-700 bg-white"
                            >
                                {selectedDateForBooking ? getRepeatOptionsForBooking(selectedDateForBooking).map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                )) : (
                                    <option value="none">請先選擇日期</option>
                                )}
                            </select>
                        </div>
                        {/* 【AI新增】備註功能 */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">5. 備註 (選填)</label>
                            <textarea value={remarkForBooking} onChange={e => setRemarkForBooking(e.target.value)} placeholder="輸入備註內容..." rows="2" className="w-full p-2 border rounded-lg text-gray-700 bg-white"></textarea>
                        </div>
                        {bookingError && <p className="text-red-500 text-sm">{bookingError}</p>}
                        <button type="submit" disabled={isSavingBooking} className="w-full bg-green-500 text-white p-2 rounded hover:bg-green-600 disabled:bg-gray-400">{isSavingBooking ? '儲存中...' : '✅ 為夥伴新增登記'}</button>
                    </form>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <h4 className="font-bold text-blue-800 mb-2">💡 使用說明</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• 選擇要幫其登記的夥伴（只顯示啟用中的夥伴）</li>
                            <li>• 選擇擺攤日期（不能選擇過去的日期）</li>
                            <li>• 選擇市場地點（可快速新增新市場）</li>
                            <li>• 可設定重複排班（每天、每週、隔週、每月、每年）</li>
                            <li>• 可加入備註說明</li>
                            <li>• 系統會自動檢查重複登記</li>
                            <li>• 登記成功後會顯示確認訊息</li>
                        </ul>
                    </div>
                </details>
                {/* 只有管理員可見的管理功能區塊 */}
                {isAdminMode && (
                  <>
                    <details className="space-y-3">
                      <summary className="font-semibold cursor-pointer">夥伴管理</summary>
                      <form onSubmit={handleAddVendor} className="space-y-3 bg-white p-3 rounded-md border">
                        <input value={newVendorId} onChange={e => setNewVendorId(e.target.value)} placeholder="新夥伴編號" className="w-full p-2 border rounded" />
                        <input value={newVendorName} onChange={e => setNewVendorName(e.target.value)} placeholder="新夥伴名稱" className="w-full p-2 border rounded" />
                        <input value={newVendorPassword} onChange={e => setNewVendorPassword(e.target.value)} placeholder="初始密碼" className="w-full p-2 border rounded" />
                        {/* 【AI修正】角色選擇下拉選單 */}
                        <label className="block text-sm font-medium text-gray-700 mb-1">角色</label>
                        <select
                            value={newVendorRole || (isNewVendorAdmin ? 'admin' : 'partner')}
                            onChange={e => {
                                const val = e.target.value;
                                setNewVendorRole(val);
                                setIsNewVendorAdmin(val === 'admin');
                            }}
                            className="w-full p-2 border rounded bg-white text-gray-700"
                        >
                            {/* 管理員可設所有角色，operator 只能設 operator/partner */}
                            {isAdminMode && <option value="admin">管理員</option>}
                            <option value="operator">營運商</option>
                            <option value="partner">夥伴</option>
                        </select>
                        {/* 【AI新增】區域權限選擇器 */}
                        <div className="border-t pt-3">
                            <RegionSelector
                                selectedRegions={newVendorRegions}
                                onChange={setNewVendorRegions}
                                showSelectAll={true}
                                className=""
                            />
                        </div>
                        {vendorError && <p className="text-red-500 text-sm">{vendorError}</p>}
                        <button type="submit" className="w-full bg-green-500 text-white p-2 rounded hover:bg-green-600">新增夥伴</button>
                      </form>
                      <div className="space-y-2 max-h-40 overflow-y-auto p-1">{vendors.map(v => {
                        // 檢查夥伴最後使用時間
                        const oneYearAgo = new Date();
                        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                        
                        const lastBooking = bookings
                            .filter(b => b.vendorId === v.id)
                            .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                        
                        const lastBookingDate = lastBooking ? new Date(lastBooking.date) : null;
                        const hasBookingsInLastYear = lastBookingDate && lastBookingDate > oneYearAgo;
                        const canDelete = v.id !== 'sd' && v.isActive === false && !hasBookingsInLastYear;
                        
                        const daysSinceLastBooking = lastBookingDate ? 
                            Math.floor((new Date() - lastBookingDate) / (1000 * 60 * 60 * 24)) : null;
                        
                        return (<div key={v.id}>{editingVendor?.id === v.id ? (
                            <div className="p-2 bg-yellow-100 rounded border border-yellow-300 space-y-2">
                                <input value={editingVendor.name} onChange={e => setEditingVendor({ ...editingVendor, name: e.target.value })} className="w-full p-1 border rounded" placeholder="夥伴名稱" />
                                {/* 【AI修正】角色選擇下拉選單（編輯） */}
                                <label className="block text-sm font-medium text-gray-700 mb-1">角色</label>
                                <select
                                    value={editingVendor.role || (editingVendor.isAdmin ? 'admin' : 'partner')}
                                    onChange={e => {
                                        const val = e.target.value;
                                        setEditingVendor({ ...editingVendor, role: val, isAdmin: val === 'admin' });
                                    }}
                                    className="w-full p-1 border rounded bg-white text-gray-700"
                                >
                                    {isAdminMode && <option value="admin">管理員</option>}
                                    <option value="operator">營運商</option>
                                    <option value="partner">夥伴</option>
                                </select>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={editingVendor.isAdmin} onChange={e => setEditingVendor({ ...editingVendor, isAdmin: e.target.checked })} />設為管理員
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={editingVendor.isActive !== false} onChange={e => setEditingVendor({ ...editingVendor, isActive: e.target.checked })} />帳號啟用
                                </label>
                                {/* 【AI新增】編輯區域權限選擇器 */}
                                <div className="border-t pt-2">
                                    <RegionSelector
                                        selectedRegions={editingVendor.regions || [...ALL_REGIONS]}
                                        onChange={(regions) => setEditingVendor({ ...editingVendor, regions })}
                                        showSelectAll={true}
                                        className=""
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={handleUpdateVendor} className="flex-1 text-xs bg-green-500 text-white py-1 px-2 rounded">儲存</button>
                                    <button onClick={() => setEditingVendor(null)} className="flex-1 text-xs bg-gray-400 text-white py-1 px-2 rounded">取消</button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col p-2 bg-white rounded border">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <span className={`font-semibold ${v.isActive === false ? 'text-gray-400 line-through' : ''}`}>{v.name}{v.role === 'operator' ? '（營運商）' : ''}</span> ({v.id}) {v.isAdmin && '👑'} {v.isActive === false && '🚫'}
                                    </div>
                                    <div className="flex gap-1">
                                        <button onClick={() => setEditingVendor(v)} className="text-xs bg-blue-500 text-white py-1 px-2 rounded">編輯</button>
                                        <button onClick={() => setResetPasswordModal({ isOpen: true, vendor: v })} className="text-xs bg-yellow-500 text-white py-1 px-2 rounded">重設密碼</button>
                                        <button onClick={() => handleToggleVendorStatus(v)} className={`text-xs py-1 px-2 rounded text-white ${v.isActive === false ? 'bg-green-600' : 'bg-orange-600'}`}>{v.isActive === false ? '啟用' : '停用'}</button>
                                        {canDelete && <button onClick={() => handleDeleteVendor(v.id)} className="text-xs bg-red-500 text-white py-1 px-2 rounded" title={`⚠️ 已停用且超過一年未使用（${daysSinceLastBooking}天前）`}>🗑️</button>}
                                    </div>
                                </div>
                                {/* 【AI新增】顯示區域權限 */}
                                <div className="text-xs text-gray-600 mt-1" dangerouslySetInnerHTML={{__html: '🗺️ 區域權限：' + formatRegionsForDisplay(v.regions || [...ALL_REGIONS])}} />
                                {lastBookingDate && (
                                    <div className="text-xs text-gray-500 mt-1">最後使用: {lastBookingDate.toLocaleDateString('zh-TW')} ({daysSinceLastBooking}天前) {hasBookingsInLastYear && '📅'}</div>
                                )}
                            </div>
                        )}</div>);
                    })}</div>
                    </details>
                    <details className="space-y-3">
                      <summary className="font-semibold cursor-pointer">市場管理</summary>
                      <form onSubmit={handleAddNewMarket} className="space-y-3 bg-white p-3 rounded-md border">
                        <select value={newMarketCity} onChange={e => setNewMarketCity(e.target.value)} className="w-full p-2 border rounded">
                            {TAIWAN_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input value={newMarketName} onChange={e => setNewMarketName(e.target.value)} placeholder="新市場名稱" className="w-full p-2 border rounded"/>
                        <div className="grid grid-cols-2 gap-2">
                            <input 
                                value={newMarketLatitude} 
                                onChange={e => setNewMarketLatitude(e.target.value)} 
                                placeholder="緯度 (選填)" 
                                type="number" 
                                step="any"
                                className="w-full p-2 border rounded"
                            />
                            <input 
                                value={newMarketLongitude} 
                                onChange={e => setNewMarketLongitude(e.target.value)} 
                                placeholder="經度 (選填)" 
                                type="number" 
                                step="any"
                                className="w-full p-2 border rounded"
                            />
                        </div>
                        <p className="text-xs text-gray-500">💡 經緯度用於拍照位置偵測，可選填</p>
                        {/* 【AI新增】自動區域分類提示 */}
                        <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                            🗺️ 系統會根據縣市自動分類市場區域：{getRegionDisplayName(getRegionByCity(newMarketCity) || '')}
                        </div>
                        {marketError && <p className="text-red-500 text-sm">{marketError}</p>}
                        <button type="submit" className="w-full bg-green-500 text-white p-2 rounded hover:bg-green-600">新增市場</button>
                      </form>
                      {/* 市場區域與縣市篩選 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">區域篩選</label>
                            <select
                                value={marketRegionFilter}
                                onChange={e => {
                                    const nextRegion = e.target.value;
                                    setMarketRegionFilter(nextRegion);
                                    if (marketCityFilter && nextRegion && getRegionByCity(marketCityFilter) !== nextRegion) {
                                        setMarketCityFilter('');
                                    }
                                }}
                                className="w-full p-2 border rounded text-sm"
                            >
                                <option value="">所有區域</option>
                                {ALL_REGIONS.map(region => (
                                    <option key={region} value={region}>
                                        {getRegionIcon(region)} {getRegionDisplayName(region)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">縣市篩選</label>
                            <select
                                value={marketCityFilter}
                                onChange={e => setMarketCityFilter(e.target.value)}
                                className="w-full p-2 border rounded text-sm"
                            >
                                <option value="">所有縣市</option>
                                {TAIWAN_CITIES
                                    .filter(city => !marketRegionFilter || getRegionByCity(city) === marketRegionFilter)
                                    .map(city => <option key={city} value={city}>{city}</option>)}
                            </select>
                        </div>
                      </div>
                      
                      <div className="space-y-2 max-h-40 overflow-y-auto p-1">
                        {markets
                            .filter(m => (!marketRegionFilter || getMarketRegion(m) === marketRegionFilter)
                                && (!marketCityFilter || m.city === marketCityFilter))
                            .map(m => (
                            <div key={m.id}>
                                {editingMarket?.id === m.id ? (
                                    <div className="p-2 bg-yellow-100 rounded border border-yellow-300 space-y-2">
                                        <select value={editingMarket.city} onChange={e => setEditingMarket({...editingMarket, city: e.target.value})} className="w-full p-1 border rounded">
                                            {TAIWAN_CITIES.map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                        <input value={editingMarket.name} onChange={e => setEditingMarket({...editingMarket, name: e.target.value})} className="w-full p-1 border rounded" />
                                        <div className="grid grid-cols-2 gap-1">
                                            <input 
                                                value={editingMarket.latitude || ''} 
                                                onChange={e => setEditingMarket({...editingMarket, latitude: e.target.value})} 
                                                placeholder="緯度" 
                                                type="number" 
                                                step="any"
                                                className="w-full p-1 border rounded text-xs"
                                            />
                                            <input 
                                                value={editingMarket.longitude || ''} 
                                                onChange={e => setEditingMarket({...editingMarket, longitude: e.target.value})} 
                                                placeholder="經度" 
                                                type="number" 
                                                step="any"
                                                className="w-full p-1 border rounded text-xs"
                                            />
                                        </div>
                                        {/* 【AI新增】編輯時的區域顯示 */}
                                        <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                                            🗺️ 當前區域：{getRegionIcon(getRegionByCity(editingMarket.city) || '')} {getRegionDisplayName(getRegionByCity(editingMarket.city) || '')}
                                            <br />
                                            💡 修改縣市會自動更新區域分類
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={handleUpdateMarket} className="flex-1 text-xs bg-green-500 text-white py-1 px-2 rounded">儲存</button>
                                            <button onClick={() => setEditingMarket(null)} className="flex-1 text-xs bg-gray-400 text-white py-1 px-2 rounded">取消</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex justify-between items-center p-2 bg-white rounded border">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold">{m.name}</span>
                                                <span className="text-sm text-gray-600">({m.city})</span>
                                                {/* 【AI新增】顯示市場區域 */}
                                                <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">
                                                    {getRegionIcon(getMarketRegion(m) || '')} {getRegionDisplayName(getMarketRegion(m) || '')}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1">
                                                {m.latitude && m.longitude && (
                                                    <span className="text-xs text-gray-500">📍 有位置資訊</span>
                                                )}
                                                {/* 【AI新增】顯示區域更新時間 */}
                                                {m.regionUpdatedAt && (
                                                    <span className="text-xs text-gray-400">
                                                        區域更新: {m.regionUpdatedAt.toDate?.() ? m.regionUpdatedAt.toDate().toLocaleDateString('zh-TW') : '未知'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => setEditingMarket(m)} className="text-xs bg-blue-500 text-white py-1 px-2 rounded">編輯</button>
                                            <button onClick={() => handleDeleteMarket(m)} className="text-xs bg-red-500 text-white py-1 px-2 rounded">刪除</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                      </div>
                    </details>
                    <details className="space-y-3">
                      <summary className="font-semibold cursor-pointer">公告管理</summary>
                      <form onSubmit={handlePostAnnouncement} className="space-y-3 bg-white p-3 rounded-md border"><textarea value={newAnnouncement} onChange={e => setNewAnnouncement(e.target.value)} placeholder="輸入新公告內容..." rows="3" className="w-full p-2 border rounded"></textarea>{announcementError && <p className="text-red-500 text-sm">{announcementError}</p>}<button type="submit" className="w-full bg-purple-500 text-white p-2 rounded hover:bg-purple-600">發布新公告</button></form>
                      <div className="space-y-2 max-h-40 overflow-y-auto p-1">{announcements.map(ann => (<div key={ann.id}>{editingAnnouncement?.id === ann.id ? (<div className="p-2 bg-yellow-100 rounded border border-yellow-300 space-y-2"><textarea value={editingAnnouncement.content} onChange={e => setEditingAnnouncement({...editingAnnouncement, content: e.target.value})} rows="2" className="w-full p-1 border rounded"></textarea><div className="flex gap-2"><button onClick={handleUpdateAnnouncement} className="flex-1 text-xs bg-green-500 text-white py-1 px-2 rounded">儲存</button><button onClick={() => setEditingAnnouncement(null)} className="flex-1 text-xs bg-gray-400 text-white py-1 px-2 rounded">取消</button></div></div>) : (<div className="flex justify-between items-center p-2 bg-white rounded border"> <p className="text-sm flex-1">{ann.content}</p> <div className="flex gap-2"><button onClick={() => setEditingAnnouncement(ann)} className="text-xs bg-blue-500 text-white py-1 px-2 rounded">編輯</button><button onClick={() => handleDeleteAnnouncement(ann.id)} className="text-xs bg-red-500 text-white py-1 px-2 rounded">刪除</button></div> </div>)}</div>))}</div>
                    </details>
                    <details className="space-y-3">
                      <summary className="font-semibold cursor-pointer">推播通知管理</summary>
                      <form onSubmit={handleSendPushNotification} className="space-y-3 bg-white p-3 rounded-md border">
                        <input value={newPushTitle} onChange={e => setNewPushTitle(e.target.value)} placeholder="推播標題" className="w-full p-2 border rounded" />
                        <textarea value={newPushBody} onChange={e => setNewPushBody(e.target.value)} placeholder="推播內容" rows="3" className="w-full p-2 border rounded"></textarea>
                        <select value={pushTargetType} onChange={e => setPushTargetType(e.target.value)} className="w-full p-2 border rounded">
                            <option value="all">發送給所有夥伴</option>
                            <option value="admins">只發送給管理員</option>
                        </select>
                        {pushError && <p className="text-red-500 text-sm">{pushError}</p>}
                        <button type="submit" className="w-full bg-orange-500 text-white p-2 rounded hover:bg-orange-600">發送推播通知</button>
                      </form>
                      <div className="space-y-2 max-h-40 overflow-y-auto p-1">
                        {pushNotifications.map(notification => (
                            <div key={notification.id} className="flex justify-between items-start p-2 bg-white rounded border">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="font-semibold text-sm">{notification.title}</p>
                                        <span className={`text-xs px-2 py-1 rounded ${notification.type === 'manual' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
                                            {notification.type === 'manual' ? '手動' : '自動'}
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">{notification.body}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {notification.createdAt?.toDate?.() ? 
                                            notification.createdAt.toDate().toLocaleString('zh-TW') : 
                                            '時間未知'
                                        }
                                    </p>
                                    {notification.successCount !== undefined && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            成功: {notification.successCount} | 失敗: {notification.failureCount} | 總計: {notification.totalTokens}
                                        </p>
                                    )}
                                </div>
                                <button onClick={() => handleDeletePushNotification(notification.id)} className="text-xs bg-red-500 text-white py-1 px-2 rounded ml-2">刪除</button>
                            </div>
                        ))}
                      </div>
                    </details>
                    
                    {/* 【AI新增】天氣通知管理區塊 */}
                    {/* Why: 提供管理員監控和手動觸發天氣預警系統的介面 */}
                    <details className="space-y-3">
                      <summary className="font-semibold cursor-pointer">🌤️ 天氣通知管理</summary>
                      <div className="bg-white p-3 rounded-md border space-y-3">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-gray-800">天氣警告系統</h4>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">自動檢測</span>
                        </div>
                        
                        <p className="text-sm text-gray-600">
                            系統會自動檢查未來兩天的天氣狀況，當偵測到惡劣天氣時，會自動通知有擺攤計畫的夥伴。
                        </p>
                        
                        <div className="border-t pt-3 space-y-2">
                          <button 
                            onClick={handleManualWeatherCheck}
                            disabled={isCheckingWeather}
                            className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 disabled:bg-gray-400 flex items-center justify-center gap-2"
                          >
                            {isCheckingWeather ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                檢查中...
                              </>
                            ) : (
                              <>
                                🌤️ 立即檢查天氣並發送通知
                              </>
                            )}
                          </button>
                          
                          <button 
                            onClick={handlePreloadWeatherData}
                            disabled={isPreloadingWeather}
                            className="w-full bg-green-500 text-white p-2 rounded hover:bg-green-600 disabled:bg-gray-400 flex items-center justify-center gap-2"
                          >
                            {isPreloadingWeather ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                預載中...
                              </>
                            ) : (
                              <>
                                📦 預載未來7天天氣資料
                              </>
                            )}
                          </button>
                        </div>
                        
                        {weatherCheckResult && (
                          <div className={`mt-3 p-3 rounded-lg text-sm whitespace-pre-wrap ${
                            weatherCheckResult.startsWith('✅') 
                              ? 'bg-green-50 border border-green-200 text-green-800'
                              : 'bg-red-50 border border-red-200 text-red-800'
                          }`}>
                            {weatherCheckResult}
                          </div>
                        )}
                        
                        {preloadResult && (
                          <div className={`mt-3 p-3 rounded-lg text-sm whitespace-pre-wrap ${
                            preloadResult.startsWith('✅') 
                              ? 'bg-green-50 border border-green-200 text-green-800'
                              : 'bg-red-50 border border-red-200 text-red-800'
                          }`}>
                            {preloadResult}
                          </div>
                        )}
                        
                        <div className="border-t pt-3">
                          <h5 className="font-medium text-gray-800 mb-2">天氣通知設定</h5>
                          <div className="text-sm text-gray-600 space-y-1">
                            <div className="flex justify-between">
                              <span>檢查時間：</span>
                              <span>每日自動（未來1-2天）</span>
                            </div>
                            <div className="flex justify-between">
                              <span>警告條件：</span>
                              <span>降雨機率 ≥70% 或有雨雷天氣</span>
                            </div>
                            <div className="flex justify-between">
                              <span>通知對象：</span>
                              <span>有受影響擺攤計畫的夥伴</span>
                            </div>
                            <div className="flex justify-between">
                              <span>資料來源：</span>
                              <span>中央氣象署開放資料</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </details>
                  </>
                )}
                {/* 營運商與管理員都可見的照片管理區塊 */}
                <details className="space-y-3">
                  <summary className="font-semibold cursor-pointer">📸 照片管理</summary>
                  <div className="bg-white p-3 rounded-md border space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium text-gray-800">夥伴拍照留底</h4>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">自動儲存</span>
                    </div>
                    
                    <p className="text-sm text-gray-600">
                        管理夥伴透過系統拍攝的照片，可查看和刪除照片記錄。
                    </p>
                    
                    <div className="border-t pt-3">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="font-medium text-gray-800">照片列表</h5>
                        <button 
                          onClick={loadVendorPhotos}
                          disabled={isLoadingPhotos}
                          className="text-sm bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 disabled:bg-gray-400"
                        >
                          {isLoadingPhotos ? '載入中...' : '🔄 重新載入'}
                        </button>
                      </div>
                      
                      {isLoadingPhotos ? (
                        <div className="text-center py-8 text-gray-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                          載入照片中...
                        </div>
                      ) : vendorPhotos.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <div className="text-2xl mb-2">📸</div>
                          暫無照片記錄
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {vendorPhotos.map(photo => {
                            // 判斷刪除權限
                            const photoDate = photo.timestamp ? new Date(photo.timestamp) : null;
                            const today = new Date();
                            const isToday = photoDate && 
                              photoDate.getFullYear() === today.getFullYear() &&
                              photoDate.getMonth() === today.getMonth() &&
                              photoDate.getDate() === today.getDate();
                            // 管理員可刪所有，operator 可刪自己區域成員，夥伴只能刪自己
                            let canDelete = false;
                            if (isAdminMode) {
                              canDelete = true; // 管理員可刪除所有照片
                            } else if (currentUser?.id === photo.vendorId) {
                              canDelete = isToday;
                            }
                            return (
                              <div key={photo.id} className="flex justify-between items-center p-2 bg-gray-50 rounded border">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm">{photo.vendorName}</span>
                                    <span className="text-xs text-gray-500">({photo.vendorId})</span>
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {photo.timestamp ? new Date(photo.timestamp).toLocaleString('zh-TW') : '時間未知'}
                                  </div>
                                  {photo.deviceInfo && (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {photo.deviceInfo.platform} | {photo.deviceInfo.language}
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <button 
                                    onClick={() => setPhotoViewModal({ isOpen: true, photo })}
                                    className="text-xs bg-blue-500 text-white py-1 px-2 rounded hover:bg-blue-600"
                                  >
                                    查看
                                  </button>
                                  {canDelete && (
                                    <button 
                                      onClick={() => handleDeletePhoto(photo)}
                                      className="text-xs bg-red-500 text-white py-1 px-2 rounded hover:bg-red-600"
                                    >
                                      刪除
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className="border-t pt-3">
                      <h5 className="font-medium text-gray-800 mb-2">照片管理說明</h5>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex justify-between">
                          <span>儲存位置：</span>
                          <span>Firestore 資料庫</span>
                        </div>
                        <div className="flex justify-between">
                          <span>照片格式：</span>
                          <span>JPEG (Base64)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>權限控制：</span>
                          <span>夥伴只能查看自己的照片，營運商可管理自己區域成員，管理員可管理全部</span>
                        </div>
                        <div className="flex justify-between">
                          <span>管理員權限：</span>
                          <span>可查看和刪除所有照片</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </details>
                {/* 【AI新增】照片查看彈窗 */}
                {photoViewModal.isOpen && <PhotoViewModal config={photoViewModal} onClose={() => setPhotoViewModal({ isOpen: false, photo: null })} onDelete={handleDeletePhoto} />}
            </div>
        </div>
    );
};

async function callGeminiAPI(prompt, setGeminiModal, functions) { 
    setGeminiModal({ isOpen: true, isLoading: true, content: '', error: '' }); 
    
    try {
        console.log('正在透過 Cloud Function 呼叫 Gemini API...');
        
        const generatePromoText = httpsCallable(functions, 'generatePromoText');
        const result = await generatePromoText({ prompt });
        
        if (result.data.success) {
            setGeminiModal({ isOpen: true, isLoading: false, content: result.data.content, error: '' });
        } else {
            throw new Error(result.data.message || '未知錯誤');
        }
    } catch (error) {
        console.error('Gemini API 呼叫失敗:', error);
        
        let userFriendlyError = error.message || '未知錯誤';
        if (userFriendlyError.includes('API key')) {
            userFriendlyError = 'API 金鑰無效或已過期，請聯繫管理員更新。';
        } else if (userFriendlyError.includes('quota')) {
            userFriendlyError = 'API 使用配額已達上限，請稍後再試。';
        } else if (userFriendlyError.includes('CORS')) {
            userFriendlyError = '網域限制問題，請確認當前網域已獲授權。';
        } else if (userFriendlyError.includes('not found') || userFriendlyError.includes('not supported')) {
            userFriendlyError = 'API 模型不可用，請稍後再試或聯繫管理員。';
        }
        
        setGeminiModal({ isOpen: true, isLoading: false, content: '', error: `AI 功能暫時無法使用：${userFriendlyError}` });
    }
}

export default App;
